import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  CircularProgress,
  Typography,
  Chip,
  Checkbox,
  ListItemText,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ListIcon from '@material-ui/icons/List';
import TimelineIcon from '@material-ui/icons/Timeline';
import CodeIcon from '@material-ui/icons/Code';
import SettingsIcon from '@material-ui/icons/Settings';
import ViewAgendaIcon from '@material-ui/icons/ViewAgenda';
import ViewListIcon from '@material-ui/icons/ViewList';
import TableChartIcon from '@material-ui/icons/TableChart';
import PeopleIcon from '@material-ui/icons/People';
import SelectAllIcon from '@material-ui/icons/SelectAll';
import ClearIcon from '@material-ui/icons/Clear';
import GetAppIcon from '@material-ui/icons/GetApp';
import DateRangeIcon from '@material-ui/icons/DateRange';
import StoriesLinksPane from './StoriesLinksPane';
import ScoreMethodologyTooltip from './ScoreMethodologyTooltip';
import StoryIntegrityBreakdown from './StoryIntegrityBreakdown';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SERVER_NAME } from './config';
import { futLogoIcon200 } from './config';
import { useAuth } from './auth/AuthContext';
import { useWatchlist } from './watchlist/WatchlistContext';
import { FaStar } from 'react-icons/fa';
import DateRangePickerPopover from './DateRangePickerPopover';
import StoryTypeIndex from './storyTypes/StoryTypeIndex';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 1480,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '4px 6px 0px',
    },
  },

  menuPaper: {
    backgroundColor: '#0b0e1e',
    color: '#e5e7ff',
    border: '1px solid rgba(77, 121, 255, 0.28)',
    borderRadius: 12,
    boxShadow: '0 0 22px rgba(77,121,255,0.10)',
  },
  menuItemDense: {
    fontSize: 10,
    paddingTop: 2,
    paddingBottom: 2,
    minHeight: 26,
  },
  menuItemTextDense: {
    fontSize: 10,
  },

  tokenInfoPanel: {
    flex: 1,
    minHeight: 0,
    borderRadius: 12,
    border: '1px solid rgba(158, 196, 255, 0.16)',
    backgroundColor: 'rgba(0,0,0,0.35)',
    padding: 10,
    overflow: 'auto',
  },
  tokenInfoPre: {
    margin: 0,
    fontSize: 10,
    lineHeight: 1.35,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#e5e7ff',
  },

  // HERO (desktop)
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,

    [theme.breakpoints.down('sm')]: {
      padding: '6px 8px 8px',
      marginBottom: 8,
    },
  },
  heroTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  heroTitle: {
    fontSize: '2.15rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  heroSubtitle: {
    marginTop: 6,
    color: '#cfd8ff',
    maxWidth: 900,
    lineHeight: 1.55,
    fontSize: 13,
  },
  heroMetaRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    color: '#9ea4d8',
    fontSize: 12,
  },
  heroMetaChip: {
    height: 22,
    fontSize: 11,
    backgroundColor: 'rgba(77, 121, 255, 0.12)',
    border: '1px solid rgba(77, 121, 255, 0.35)',
    color: '#e5e7ff',
  },

  typeCountsRow: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  typeCountChip: {
    height: 24,
    fontSize: 11,
    backgroundColor: 'rgba(77, 121, 255, 0.10)',
    border: '1px solid rgba(77, 121, 255, 0.30)',
    color: '#e5e7ff',
    '&:hover': {
      backgroundColor: 'rgba(77, 121, 255, 0.10)',
    },
    '&:active': {
      backgroundColor: 'rgba(77, 121, 255, 0.10)',
    },
    '&:focus': {
      outline: 'none',
    },
    '&.Mui-focusVisible': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  typeCountChipActive: {
    height: 24,
    fontSize: 11,
    backgroundColor: 'rgba(255, 229, 92, 0.22)',
    border: '1px solid rgba(255, 229, 92, 0.95)',
    boxShadow: '0 0 14px rgba(255, 229, 92, 0.20)',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 229, 92, 0.22)',
    },
    '&:active': {
      backgroundColor: 'rgba(255, 229, 92, 0.22)',
    },
    '&:focus': {
      outline: 'none',
    },
    '&.Mui-focusVisible': {
      outline: 'none',
      boxShadow: '0 0 14px rgba(255, 229, 92, 0.20)',
    },
  },
  typeCountText: {
    fontSize: 11,
    fontWeight: 400,
  },
  typeCountNumber: {
    fontSize: 14,
    fontWeight: 900,
  },

  // Mobile compact header row (replaces big title/desc)
  mobileHeaderRow: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      marginBottom: 8,
    },
  },
  mobileHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#cfd8ff',
    fontSize: 12,
  },

  // CONTROLS (desktop grid)
  controlsRow: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 10,
  },

  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
  },

  controlLabel: {
    fontSize: 10,
    color: '#9ea4d8',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 2,
    paddingLeft: 2,

    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    },
  },

  controlField: {
    flex: '0 0 auto',
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      background: 'rgba(5, 8, 20, 0.62)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.10)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-root': {
      color: '#9ea4d8',
    },

    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      flex: '0 0 auto',
      '& .MuiInputBase-input': {
        fontSize: 11,
        padding: '6px 8px',
      },
      '& .MuiSelect-root': {
        fontSize: 11,
      },
    },
  },

  // Make the search field dominate on mobile
  controlFieldSearch: {
    flex: '1 1 auto',
    minWidth: 260,
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      flex: '1 1 0%',
    },
  },

  // MOBILE: stacked controls (search on its own line, filters on next)
  mobileControls: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  mobileSearchRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  mobileSearchInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  mobileFiltersRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 6,
    overflowX: 'auto',
    paddingBottom: 2,
  },

  mobileForensicsWrapper: {
    marginTop: 10,
    height: 'calc(var(--vvh, 1vh) * 42)',
    maxHeight: 'calc(var(--vvh, 1vh) * 42)',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 'env(safe-area-inset-bottom)',
    '@supports (height: 100svh)': {
      height: '42svh',
      maxHeight: '42svh',
    },
    '@supports (height: 100dvh)': {
      height: '42dvh',
      maxHeight: '42dvh',
    },
  },

  // MAIN
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 18,
    flex: 1,
    minHeight: 0,

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 12,
    },
  },

  // BRIEF
  briefPanel: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  sectionTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '0 2px',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#e5e7ff',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  sectionHint: {
    fontSize: 12,
    color: '#9ea4d8',
  },

  storyCard: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.26)',
    background: 'rgba(10, 14, 30, 0.92)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: 'auto',
    cursor: 'pointer',
    transition: 'transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: 'rgba(77, 121, 255, 0.55)',
      boxShadow: '0 0 18px rgba(77,121,255,0.12)',
    },
  },
  storyCardSelected: {
    borderColor: 'rgba(158, 247, 197, 0.55)',
    boxShadow: '0 0 20px rgba(158,247,197,0.12)',
  },
  storyCardTop: {
    padding: '14px 14px 10px',
    background:
      'linear-gradient(90deg, rgba(77,121,255,0.22), rgba(10,14,30,0.96))',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  storyCardTitle: {
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: -0.1,
    marginBottom: 8,
  },
  storyCardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 11,
    color: '#9ea4d8',
  },
  chipType: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    border: '1px solid rgba(77, 121, 255, 0.45)',
    color: '#ffe55c',
    fontWeight: 800,
  },
  chipChain: {
    height: 20,
    fontSize: 10,
    backgroundColor: 'rgba(15, 205, 255, 0.10)',
    border: '1px solid rgba(15, 205, 255, 0.35)',
    color: '#cfd3ff',
    fontWeight: 700,
  },
  storyCardBody: {
    padding: '12px 14px 14px',
  },
  teaser: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.55,
    opacity: 0.92,
    whiteSpace: 'pre-wrap',
  },

  // FEED
  feedPanel: {
    marginTop: 6,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  feedHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  feedBody: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minHeight: 0,
    touchAction: 'pan-y',
    overscrollBehaviorX: 'none',
  },
  feedBodySummary: {
    padding: 6,
    gap: 4,
  },
  feedRow: {
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: 10,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
    '&:hover': { borderColor: 'rgba(77,121,255,0.4)' },
  },
  feedRowSummary: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  feedRowSelected: {
    background: 'rgba(16, 20, 40, 0.96)',
    borderColor: 'rgba(158, 247, 197, 0.75)',
    boxShadow: '0 0 18px rgba(158,247,197,0.22)',
  },
  feedRowTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    color: '#e5e7ff',
  },
  feedRowTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  feedRowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 11,
    color: '#9ea4d8',
  },

  // DETAIL
  detailPanel: {
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.28), rgba(10,14,30,0.98))',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  detailTitle: {
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: -0.2,
    marginBottom: 6,
  },
  detailMeta: {
    fontSize: 12,
    color: '#cfd3ff',
    marginBottom: 10,
  },
  narrativeBlock: {
    padding: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  bulletList: {
    margin: 0,
    paddingLeft: 18,
  },
  bulletItem: {
    marginBottom: 8,
  },
  bulletLabel: {
    color: '#9ef7c5',
    fontWeight: 800,
    marginRight: 6,
  },

  forensicsPanel: {
    minHeight: 0,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.22), rgba(5,8,20,0.98))',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflow: 'hidden',
  },
  forensicsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  forensicsTitle: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: '#e5e7ff',
  },
  forensicsTokenLabel: {
    fontSize: 11,
    color: '#9ea4d8',
  },
  forensicsTokenValue: {
    fontSize: 12,
    fontFamily: 'monospace',
    backgroundColor: 'rgba(5,8,20,0.9)',
    borderRadius: 8,
    padding: '6px 8px',
    wordBreak: 'break-all',
  },
  forensicsEmpty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    fontSize: 12,
    color: '#9ea4d8',
    textAlign: 'center',
  },

  actionsRow: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    paddingTop: 12,
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  ghostButton: {
    textTransform: 'none',
    fontWeight: 700,
    color: '#cfd3ff',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '10px 14px',
  },

  secondaryText: {
    fontSize: 11,
    color: '#9ea4d8',
  },

  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 12,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ea4d8',
    fontSize: 14,
  },
}));

const shortenAddress = (addr) => {
  if (!addr) return '';
  const a = String(addr);
  if (a.length <= 12) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`; // 0x1234…abcd
};

function formatRangeLabel(isoStart, isoEnd) {
  try {
    if (!isoStart || !isoEnd) return '';
    const s = new Date(String(isoStart));
    const e = new Date(String(isoEnd));
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return '';
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(s)} – ${fmt(e)}`;
  } catch (err) {
    return '';
  }
}

const isLikelyAddress = (s) =>
  typeof s === 'string' && /^0x[a-fA-F0-9]{40}$/.test(s);

const safeJsonParse = (s) => {
  try { return JSON.parse(s); } catch { return null; }
};

function toSafeText(val) {
  if (val == null) return '';
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return '';
    if (/^\[object\s+[^\]]+\]$/i.test(t)) return '';
    return t;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return '';
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const date = new Date(ts.replace(' ', 'T'));
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.max(Math.floor(diffMs / 1000), 0);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
}

function summarizeStory(story) {
  if (!story) return '';
  if (story.ai_narrative && typeof story.ai_narrative === 'string') {
    const text = story.ai_narrative.trim();
    if (!text) return '';
    return text.length > 220 ? `${text.slice(0, 217)}…` : text;
  }
  const sub = toSafeText(story.subtitle);
  if (sub) return sub;
  return '';
}

function getSandwichMetricsLine(story) {
  if (!story || story.story_type !== 'SANDWICH') return '';
  const meta = (story.meta && typeof story.meta === 'object') ? story.meta : null;
  if (!meta) return '';

  const parts = [];
  const botShort = meta.bot_address_short || (meta.bot_address ? `${String(meta.bot_address).slice(0, 6)}…${String(meta.bot_address).slice(-4)}` : null);
  const victimShort = meta.victim_address_short || (meta.victim_address ? `${String(meta.victim_address).slice(0, 6)}…${String(meta.victim_address).slice(-4)}` : null);
  const token = meta.token_symbol || (meta.token_address ? `${String(meta.token_address).slice(0, 6)}…${String(meta.token_address).slice(-4)}` : null);
  const profit = Number(meta.profit_usd_est);
  const block = meta.block_number;

  if (botShort) parts.push(`Bot: ${botShort}`);
  if (victimShort) parts.push(`Victim: ${victimShort}`);
  if (token) parts.push(`Token: ${token}`);
  if (Number.isFinite(profit) && profit > 0) parts.push(`~$${profit < 1 ? profit.toFixed(2) : Math.round(profit)} est. profit`);
  if (block) parts.push(`Block ${block}`);

  return parts.length ? parts.join(' · ') : '';
}

function getAirdropMetricsLine(story) {
  if (!story || story.story_type !== 'AIRDROP') return '';

  const meta = (story.meta && typeof story.meta === 'object') ? story.meta : null;
  if (!meta) return '';

  const tpr = Number(meta.transfers_per_recipient);
  const tprLabel = Number.isFinite(tpr) && tpr > 0 ? `${tpr.toFixed(2)} tx/recipient` : '';

  const amountShape = meta.amount_shape && typeof meta.amount_shape === 'object' ? meta.amount_shape : null;
  const shape = amountShape ? toSafeText(amountShape.shape) : '';
  const uniqAmounts = amountShape ? Number(amountShape.uniq_amounts) : NaN;
  const top1Share = amountShape ? Number(amountShape.top1_share) : NaN;

  const shapeParts = [];
  if (shape) shapeParts.push(shape);
  if (Number.isFinite(uniqAmounts) && uniqAmounts > 0) shapeParts.push(`${uniqAmounts} ${uniqAmounts === 1 ? 'amount' : 'amounts'}`);
  if (Number.isFinite(top1Share) && top1Share >= 0.01) shapeParts.push(`${Math.round(top1Share * 100)}% top`);
  else if (Number.isFinite(top1Share) && top1Share > 0) shapeParts.push('<1% top');
  const shapeLabel = shapeParts.length ? `Amounts: ${shapeParts.join(', ')}` : '';

  const parts = [];
  if (tprLabel) parts.push(tprLabel);
  if (shapeLabel) parts.push(shapeLabel);

  return parts.length ? parts.join(' · ') : '';
}

function parseNarrativeBullets(text) {
  if (!text || typeof text !== 'string') return null;
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const bullets = lines
    .filter((l) => l.startsWith('-'))
    .map((l) => l.replace(/^-+\s*/, ''));

  if (bullets.length >= 2) {
    const mapped = bullets.map((b) => {
      const idx = b.indexOf(':');
      if (idx === -1) {
        const cleanBody = b.replace(/\*\*/g, '').trim();
        return { label: '', body: cleanBody };
      }
      const rawLabel = b.slice(0, idx).trim();
      const rawBody = b.slice(idx + 1).trim();
      const cleanLabel = rawLabel.replace(/\*\*/g, '').trim();
      const cleanBody = rawBody.replace(/\*\*/g, '').trim();
      return {
        label: cleanLabel,
        body: cleanBody,
      };
    });
    return mapped.slice(0, 3);
  }
  return null;
}

function getFullNarrative(story) {
  if (!story) return '';
  if (story.ai_narrative && typeof story.ai_narrative === 'string') {
    const t = story.ai_narrative.trim();
    if (t) return t;
  }
  const extra = story.ai_extra;
  if (extra && typeof extra === 'object') {
    const candidates = [extra.narrative, extra.full_narrative, extra.body];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
    }
  }
  const sub = toSafeText(story.subtitle);
  if (sub) return sub;
  return '';
}

function getLinksTarget(story) {
  if (!story) return { address: null, kind: 'story', tokenLabel: null };
  const meta = story.meta || {};
  const wallet = meta.wallet || meta.address;
  const tokenAddress =
    meta.token_address ||
    meta.token ||
    meta.contract ||
    story.primary_token ||
    null;

  let address = null;
  let kind = 'story';
  if (tokenAddress) {
    address = tokenAddress;
    kind = 'token';
  } else if (wallet) {
    address = wallet;
    kind = 'wallet';
  }

  let tokenLabel = null;
  if (kind === 'token') tokenLabel = meta.symbol || meta.token_symbol || null;

  return { address, kind, tokenLabel };
}

function buildLaunchLabel(story) {
  if (!story) return 'Explore in E3D';
  if (story.story_type === 'FLOW') return 'Explore wallet graph';
  const { kind, tokenLabel } = getLinksTarget(story);
  if (tokenLabel) return `Explore ${tokenLabel} evidence`;
  if (kind === 'wallet') return 'Explore wallet evidence';
  if (kind === 'token') return 'Explore token evidence';
  return 'Explore evidence';
}

function escapeRegExp(input) {
  return String(input).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderHighlightedText(text, query) {
  const raw = text == null ? '' : String(text);
  const q = query == null ? '' : String(query).trim();
  if (!q) return raw;
  if (!raw) return raw;

  const safe = escapeRegExp(q);
  const re = new RegExp(`(${safe})`, 'ig');
  const parts = raw.split(re);
  if (!parts || parts.length <= 1) return raw;

  return (
    <>
      {parts.map((part, idx) => {
        const isMatch = part && part.toLowerCase() === q.toLowerCase();
        if (!isMatch) return <React.Fragment key={idx}>{part}</React.Fragment>;
        return (
          <span
            key={idx}
            style={{
              backgroundColor: 'rgba(255, 229, 92, 0.35)',
              borderBottom: '1px solid rgba(255, 229, 92, 0.9)',
              borderRadius: 4,
              padding: '0 1px',
            }}
          >
            {part}
          </span>
        );
      })}
    </>
  );
}

function renderLabeledTeaser(text, query) {
  const raw = toSafeText(text);
  if (!raw) return raw;

  // Insert a newline before any "X:" label that begins a new sentence.
  // We do not insert a newline for the very first sentence.
  const withBreaks = raw.replace(/([.!?])\s+([A-Z][A-Za-z0-9 \-/]{0,40}:)\s*/g, (m, punc, label) => {
    return `${punc}\n${label} `;
  });

  const lines = withBreaks
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const labelRe = /^([A-Z][A-Za-z0-9 \-/]{0,40}:)\s*(.*)$/;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '116px 1fr',
        columnGap: 8,
        rowGap: 4,
        lineHeight: 1.45,
      }}
    >
      {lines.map((line, idx) => {
        const m = line.match(labelRe);
        if (!m) {
          return (
            <React.Fragment key={idx}>
              <div style={{ gridColumn: '1 / 3' }}>{renderHighlightedText(line, query)}</div>
            </React.Fragment>
          );
        }

        const label = m[1] || '';
        const rest = m[2] || '';
        return (
          <React.Fragment key={idx}>
            <div style={{ fontWeight: 800, color: '#e5e7ff' }}>{renderHighlightedText(label, query)}</div>
            <div style={{ color: '#cfd8ff' }}>{rest ? renderHighlightedText(rest, query) : null}</div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function getKeyLinesForSummary(text) {
  const raw = toSafeText(text);
  if (!raw) return [];

  // Normalize sentence-label breaks similar to renderLabeledTeaser.
  const withBreaks = raw.replace(/([.!?])\s+([A-Z][A-Za-z0-9 \-/]{0,40}:)\s*/g, (m, punc, label) => {
    return `${punc}\n${label} `;
  });
  const lines = withBreaks
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const pick = (prefix) => lines.find((l) => l.toLowerCase().startsWith(prefix));

  const what = pick('what happened:') || pick('what happened') || null;
  const why = pick('why it matters:') || pick('why it matters') || null;

  const out = [];
  if (what) out.push(what);
  if (why) out.push(why);
  if (!out.length && lines[0]) out.push(lines[0]);
  return out.slice(0, 2);
}

const renderMatchedAddresses = (story) => {
  const list = story && Array.isArray(story.matched_addresses) ? story.matched_addresses : [];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      <span style={{ fontSize: 11, color: '#9ea4d8' }}>Matched</span>
      {list.slice(0, 6).map((a) => (
        <Chip
          key={a}
          size="small"
          label={shortenAddress(a)}
          style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.35)', color: '#ffd700', height: 20 }}
        />
      ))}
      {list.length > 6 && (
        <span style={{ fontSize: 11, color: '#9ea4d8' }}>+{list.length - 6}</span>
      )}
    </div>
  );
};

export default function StoriesPage() {
  const [searchParams] = useSearchParams();
  const view = String(searchParams.get('view') || '').trim().toLowerCase();

  if (view === 'types' || view === 'story-types' || view === 'index') {
    return <StoryTypeIndex />;
  }

  return <StoriesRawStream />;
}

const StoriesRawStream = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isPrintMode = useMemo(() => {
    try {
      const params = new URLSearchParams(location && location.search ? location.search : '');
      const v = params.get('print');
      return v === '1' || v === 'true';
    } catch (e) {
      return false;
    }
  }, [location]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isMobileLandscape = isMobile && !isPortrait;

  const { isAuthenticated, roles } = useAuth();
  const { watchlistOnly, setWatchlistOnly, watchedKeys } = useWatchlist();

  const showLoginDialog = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.globalShowLogin === 'function') {
        window.globalShowLogin(true);
      }
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('force-login-dialog-render'));
      }
    } catch (e) {
    }
  }, []);

  const showMembersOnlyDialog = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.globalShowMembersOnly === 'function') {
        window.globalShowMembersOnly(true);
      }
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('force-members-only-dialog-render'));
      }
    } catch (e) {
    }
  }, []);

  const handleToggleWatchlistOnly = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch (err) {
    }

    const run = async () => {
      try {
        const resp = await fetch(`${SERVER_NAME}/auth/status`, { credentials: 'include' });
        const data = resp && resp.ok ? await resp.json().catch(() => null) : null;
        const ok = !!(data && data.authenticated);
        if (!ok) {
          showMembersOnlyDialog();
          return;
        }
      } catch (err2) {
        showMembersOnlyDialog();
        return;
      }

      setWatchlistOnly((prev) => !prev);
    };

    // Fire and forget; UI is controlled by state updates.
    run();
  }, [setWatchlistOnly, showMembersOnlyDialog]);

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [requestedStoryId, setRequestedStoryId] = useState(null);
  const [pinnedStoryId, setPinnedStoryId] = useState(null);
  const [autoScrolledStoryId, setAutoScrolledStoryId] = useState(null);
  const storiesRef = useRef([]);

  useEffect(() => {
    storiesRef.current = Array.isArray(stories) ? stories : [];
  }, [stories]);

  const [maxLookbackDays, setMaxLookbackDays] = useState(null);
  const [storiesTierHeader, setStoriesTierHeader] = useState(null);

  const [dateRangeValue, setDateRangeValue] = useState(null);
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
  const [signInAnchorEl, setSignInAnchorEl] = useState(null);
  const [rangeUpgradeMsg, setRangeUpgradeMsg] = useState('');

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) return;
      const cachedDays = String(window.sessionStorage.getItem('e3d_max_lookback_days') || '').trim();
      if (cachedDays) {
        const n = Number(cachedDays);
        if (Number.isFinite(n) && n > 0) setMaxLookbackDays(n);
      }
      const cachedTier = String(window.sessionStorage.getItem('e3d_tier') || '').trim();
      if (cachedTier) setStoriesTierHeader(cachedTier);

      const raw = String(window.sessionStorage.getItem('e3d_date_range') || '').trim();
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.start && parsed.end) {
        setDateRangeValue({ start: String(parsed.start), end: String(parsed.end) });
      }
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) return;
      if (dateRangeValue && dateRangeValue.start && dateRangeValue.end) {
        window.sessionStorage.setItem('e3d_date_range', JSON.stringify({ start: dateRangeValue.start, end: dateRangeValue.end }));
      } else {
        window.sessionStorage.removeItem('e3d_date_range');
      }
    } catch (e) {
    }
  }, [dateRangeValue]);

  const effectiveMaxLookbackDays = useMemo(() => {
    const fromHeader = Number(maxLookbackDays);
    if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.floor(fromHeader);
    try {
      const r = Array.isArray(roles) ? roles : [];
      if (r.includes('enterprise')) return 90;
      if (r.includes('premium')) return 30;
      if (isAuthenticated) return 7;
    } catch (e) {
    }
    return 7;
  }, [maxLookbackDays, roles, isAuthenticated]);

  const isPremiumPlus = useMemo(() => {
    try {
      const r = Array.isArray(roles) ? roles : [];
      return r.includes('premium') || r.includes('enterprise');
    } catch (e) {
      return false;
    }
  }, [roles]);

  const isRangeActive = !!(dateRangeValue && dateRangeValue.start && dateRangeValue.end);

  const [onlyWithDeepDives, setOnlyWithDeepDives] = useState(false);

  const [derivedOpenById, setDerivedOpenById] = useState(() => ({}));
  const [derivedBySourceId, setDerivedBySourceId] = useState(() => ({}));
  const [derivedLoadingById, setDerivedLoadingById] = useState(() => ({}));
  const [derivedErrorById, setDerivedErrorById] = useState(() => ({}));

  const derivedBySourceIdRef = useRef(derivedBySourceId);
  const derivedLoadingByIdRef = useRef(derivedLoadingById);
  useEffect(() => {
    derivedBySourceIdRef.current = derivedBySourceId;
  }, [derivedBySourceId]);
  useEffect(() => {
    derivedLoadingByIdRef.current = derivedLoadingById;
  }, [derivedLoadingById]);

  const [groupedStoryIds, setGroupedStoryIds] = useState(() => new Set());
  const [groupModeEnabled, setGroupModeEnabled] = useState(false);

  // The "current" primary token for the inline forensics view. This starts
  // as the story's primary_token but can be changed by clicking nodes in the
  // graph or list.
  const [currentPrimaryToken, setCurrentPrimaryToken] = useState(null);
  const [currentChain, setCurrentChain] = useState('ETH');
  const [selectionHistory, setSelectionHistory] = useState([]);
  const [selectionIndex, setSelectionIndex] = useState(-1); // index into selectionHistory
  const [expandedHistory, setExpandedHistory] = useState(false);
  const [primaryTokenMeta, setPrimaryTokenMeta] = useState(null);
  const [primaryTokenMetaLoading, setPrimaryTokenMetaLoading] = useState(false);
  const [forensicsUpdating, setForensicsUpdating] = useState(false);
  const [forensicsSnapshot, setForensicsSnapshot] = useState(null);
  const handleForensicsSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    setForensicsSnapshot((prev) => {
      const prevToken = prev && prev.token ? String(prev.token) : '';
      const nextToken = snapshot && snapshot.token ? String(snapshot.token) : '';
      const prevNodes = prev && prev.graphData && Array.isArray(prev.graphData.nodes) ? prev.graphData.nodes.length : 0;
      const nextNodes = snapshot && snapshot.graphData && Array.isArray(snapshot.graphData.nodes) ? snapshot.graphData.nodes.length : 0;
      const prevLinks = prev && prev.graphData && Array.isArray(prev.graphData.links) ? prev.graphData.links.length : 0;
      const nextLinks = snapshot && snapshot.graphData && Array.isArray(snapshot.graphData.links) ? snapshot.graphData.links.length : 0;
      const prevList = prev && Array.isArray(prev.tokenList) ? prev.tokenList.length : 0;
      const nextList = snapshot && Array.isArray(snapshot.tokenList) ? snapshot.tokenList.length : 0;
      if (prevToken === nextToken && prevNodes === nextNodes && prevLinks === nextLinks && prevList === nextList) {
        return prev;
      }
      return snapshot;
    });
  }, []);

  const [forensicsFullScreen, setForensicsFullScreen] = useState(false);

  // Forensics inline view: list vs graph mode. Default to graph on desktop,
  // list on mobile since the graph is heavier.
  const [forensicsMode, setForensicsMode] = useState(isMobile ? 'both' : 'both');

  useEffect(() => {
    if (!isPrintMode) return;
    setForensicsMode('list');
  }, [isPrintMode]);

  const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
  const [tokenInfoError, setTokenInfoError] = useState('');
  const [tokenInfoText, setTokenInfoText] = useState('');
  const [tokenInfoCopied, setTokenInfoCopied] = useState(false);
  const [tokenInfoSource, setTokenInfoSource] = useState('');
  const tokenInfoCacheRef = useRef({});

  useEffect(() => {
    let cancelled = false;

    const addr = currentPrimaryToken || '';
    const cacheKey = `${currentChain}:${String(addr).toLowerCase()}`;

    if (forensicsMode !== 'tokeninfo') return undefined;
    if (!addr) {
      setTokenInfoLoading(false);
      setTokenInfoError('');
      setTokenInfoText('');
      setTokenInfoSource('');
      return undefined;
    }

    const cached = tokenInfoCacheRef.current[cacheKey];
    if (cached) {
      setTokenInfoLoading(false);
      setTokenInfoError('');
      setTokenInfoText(cached);
      setTokenInfoSource('cached');
      return undefined;
    }

    setTokenInfoLoading(true);
    setTokenInfoError('');
    setTokenInfoText('');
    setTokenInfoSource('');

    const qs = new URLSearchParams({ chain: currentChain, address: addr }).toString();

    const fetchPretty = async (url, sourceLabel) => {
      const resp = await fetch(url, { credentials: 'same-origin' });
      if (!resp.ok) {
        const err = new Error(`HTTP ${resp.status}`);
        err.status = resp.status;
        err.body = await resp.text().catch(() => '');
        throw err;
      }
      const raw = await resp.text();
      let pretty = raw;
      try {
        const parsed = JSON.parse(raw);
        pretty = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // keep raw
      }
      if (cancelled) return;
      tokenInfoCacheRef.current[cacheKey] = pretty;
      setTokenInfoText(pretty);
      setTokenInfoSource(sourceLabel);
    };

    (async () => {
      try {
        await fetchPretty(`${SERVER_NAME}/getTokenInfoJson?${qs}`, 'coingecko');
      } catch (e) {
        const status = Number(e && e.status) || 0;
        if (status === 404) {
          try {
            await fetchPretty(`${SERVER_NAME}/getOnchainTokenInfo?${qs}`, 'onchain');
            return;
          } catch (e2) {
            if (cancelled) return;
            setTokenInfoError(String(e2 && e2.body ? e2.body : (e2 && e2.message ? e2.message : e2)));
            setTokenInfoText('');
            setTokenInfoSource('');
            return;
          }
        }
        if (cancelled) return;
        setTokenInfoError(String(e && e.body ? e.body : (e && e.message ? e.message : e)));
        setTokenInfoText('');
        setTokenInfoSource('');
      } finally {
        if (cancelled) return;
        setTokenInfoLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [forensicsMode, currentPrimaryToken, currentChain]);

  // Stories list view: expanded cards vs compact summary rows.
  const [storiesViewMode, setStoriesViewMode] = useState(() => {
    try {
      const raw = (typeof window !== 'undefined' && window.localStorage)
        ? String(window.localStorage.getItem('e3d_stories_view_mode') || '')
        : '';
      const v = raw === 'summary' ? 'list' : raw;
      if (v === 'cards' || v === 'list' || v === 'table') return v;
      return 'cards';
    } catch (e) {
      return 'cards';
    }
  }); // 'cards' | 'list' | 'table'

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('e3d_stories_view_mode', String(storiesViewMode || 'cards'));
      }
    } catch (e) {
    }
  }, [storiesViewMode]);

  const [tableSortKey, setTableSortKey] = useState('score');
  const [tableSortDir, setTableSortDir] = useState('desc');
  const [expandedTableStoryId, setExpandedTableStoryId] = useState(null);

  const [sortMode, setSortMode] = useState('score');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [backendSearch, setBackendSearch] = useState('');
  const [relatedAddressesExpandedById, setRelatedAddressesExpandedById] = useState({});

  const [storiesDate, setStoriesDate] = useState('');
  const [storiesTimeRange, setStoriesTimeRange] = useState('24h');

  const storiesSinceIso = useMemo(() => {
    const now = new Date();
    const toIso = (d) => {
      try {
        return d.toISOString();
      } catch (e) {
        return '';
      }
    };

    if (storiesTimeRange === '1h') {
      return toIso(new Date(now.getTime() - 1 * 60 * 60 * 1000));
    }
    if (storiesTimeRange === '12h') {
      return toIso(new Date(now.getTime() - 12 * 60 * 60 * 1000));
    }
    if (storiesTimeRange === '24h') {
      return toIso(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    }
    return '';
  }, [storiesTimeRange]);

  useEffect(() => {
    const qs = new URLSearchParams((location && location.search) ? location.search : '');
    const dateParam = String(qs.get('date') || '').trim();
    const typesParam = String(qs.get('types') || '').trim();
    const storyIdParam = String(qs.get('storyId') || '').trim();
    const tokenAddressParam = String(qs.get('tokenAddress') || '').trim();
    const tokenAddressState = String(location && location.state && location.state.tokenAddress ? location.state.tokenAddress : '').trim();
    const tokenAddress = tokenAddressParam || tokenAddressState;

    if (storyIdParam) {
      setPinnedStoryId(storyIdParam);
      setRequestedStoryId(storyIdParam);
      setSelectedId(storyIdParam);
    } else {
      setRequestedStoryId(null);
      setPinnedStoryId(null);
    }

    if (dateParam) {
      setStoriesDate(dateParam);
      setStoriesTimeRange('today');
    }

    if (typesParam) {
      const parts = typesParam
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      if (parts.length) {
        setSelectedTypes(Array.from(new Set(parts)));
      }
    }

    if (tokenAddress) {
      setSearch(tokenAddress);
      setBackendSearch(tokenAddress);
    }
  }, [location && location.search]);

  useEffect(() => {
    const sid = requestedStoryId ? String(requestedStoryId).trim() : '';
    if (!sid) return;

    const exists = (Array.isArray(storiesRef.current) ? storiesRef.current : []).some((s) => String(s && s.id) === sid);
    if (exists) return;

    let mounted = true;
    const run = async () => {
      try {
        const qs = new URLSearchParams({ storyId: sid }).toString();
        const resp = await fetch(`${SERVER_NAME}/stories/byId?${qs}`, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!mounted || !data || !data.id) return;

        setStories((prev) => {
          const list = Array.isArray(prev) ? prev.slice() : [];
          const already = list.some((s) => String(s && s.id) === String(data.id));
          if (already) return list;
          return [data, ...list];
        });
      } catch (e) {
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [requestedStoryId]);

  useEffect(() => {
    const pad = (n) => String(n).padStart(2, '0');
    const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const todayStr = ymd(new Date());
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const yesterdayStr = ymd(yesterday);

    if (storiesTimeRange === 'today') {
      setStoriesDate(todayStr);
    } else if (storiesTimeRange === 'yesterday') {
      setStoriesDate(yesterdayStr);
    } else {
      setStoriesDate('');
    }
  }, [storiesTimeRange]);
  const autoSearchScope = useMemo(() => {
    const q = String(backendSearch || '').trim();
    if (!q) return null;
    const looksLikeAddress = /^0x[0-9a-fA-F]{40}$/.test(q);
    return looksLikeAddress ? 'any' : null;
  }, [backendSearch]);

  useEffect(() => {
    if (!(isMobile && isPortrait)) return;

    const setVar = () => {
      try {
        const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;
        const h = (vv && Number(vv.height)) ? Number(vv.height) : (typeof window !== 'undefined' ? Number(window.innerHeight) : 0);
        if (!h) return;
        const vvh = h * 0.01;
        document.documentElement.style.setProperty('--vvh', `${vvh}px`);
      } catch (e) {
      }
    };

    setVar();

    const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;
    try {
      window.addEventListener('resize', setVar);
      if (vv) {
        vv.addEventListener('resize', setVar);
        vv.addEventListener('scroll', setVar);
      }
    } catch (e) {
    }

    return () => {
      try {
        window.removeEventListener('resize', setVar);
        if (vv) {
          vv.removeEventListener('resize', setVar);
          vv.removeEventListener('scroll', setVar);
        }
      } catch (e) {
      }
    };
  }, [isMobile, isPortrait]);

  useEffect(() => {
    const resizeStories = () => {
      const storiesContainer = document.getElementById('storiesContainer');
      if (!storiesContainer) return;

      const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;
      const totalHeight = (vv && Number(vv.height)) ? Number(vv.height) : window.innerHeight;

      const e3dHeader = document.getElementById('e3dHeader');
      const adBannerContainer = document.getElementById('adBannerContainer');
      const premiumBannerContainer = document.getElementById('premiumBannerContainer');

      const otherHeight =
        (e3dHeader ? e3dHeader.clientHeight : 0) +
        (adBannerContainer ? adBannerContainer.clientHeight : 0) +
        (premiumBannerContainer ? premiumBannerContainer.clientHeight : 0);

      const availableHeight = totalHeight - otherHeight - 20;
      const clampedHeight = availableHeight > 0 ? availableHeight : totalHeight;

      storiesContainer.style.height = `${clampedHeight}px`;
      storiesContainer.style.maxHeight = `${clampedHeight}px`;
    };

    window.addEventListener('resize', resizeStories);
    resizeStories();
    return () => window.removeEventListener('resize', resizeStories);
  }, [watchlistOnly]);

  useEffect(() => {
    let isMounted = true;
    const q = backendSearch.trim();

    setLoading(true);
    setError(null);

    const params = new URLSearchParams();

    if (isRangeActive) {
      params.set('since', String(dateRangeValue.start));
      params.set('until', String(dateRangeValue.end));
    }

    if (q) {
      params.set('limit', '100');
      params.set('q', q);
      if (autoSearchScope) params.set('scope', autoSearchScope);
    } else {
      params.set('limit', '200');
      if (!isRangeActive && storiesDate) {
        params.set('date', storiesDate);
      } else if (!isRangeActive && storiesSinceIso) {
        params.set('since', storiesSinceIso);
      }
    }

    const endpoint = watchlistOnly
      ? `${SERVER_NAME}/stories/watchlist?${params.toString()}`
      : `${SERVER_NAME}/stories?${params.toString()}`;

    fetch(endpoint, {
      credentials: 'include',
    })
      .then((res) => {
        if (res.status === 401 && watchlistOnly) {
          showMembersOnlyDialog();
          setWatchlistOnly(false);
          throw new Error('HTTP 401');
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        try {
          const tier = res.headers.get('x-e3d-tier');
          const days = res.headers.get('x-e3d-max-lookback-days');
          if (tier) setStoriesTierHeader(String(tier));
          if (days) setMaxLookbackDays(Number(days));
          if (typeof window !== 'undefined' && window.sessionStorage) {
            if (tier) window.sessionStorage.setItem('e3d_tier', String(tier));
            if (days) window.sessionStorage.setItem('e3d_max_lookback_days', String(days));
          }
        } catch (e) {
        }

        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setStories((prev) => {
          const pinnedSid = pinnedStoryId ? String(pinnedStoryId) : '';
          if (!pinnedSid) return list;

          const pinnedFromFetch = list.find((s) => String(s && s.id) === pinnedSid) || null;
          const pinnedFromPrev = (Array.isArray(prev) ? prev : []).find((s) => String(s && s.id) === pinnedSid) || null;
          const pinned = pinnedFromFetch || pinnedFromPrev;
          if (!pinned) return list;

          const rest = list.filter((s) => String(s && s.id) !== pinnedSid);
          return [pinned, ...rest];
        });
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Error loading stories:', err);
        if (watchlistOnly && String(err && err.message || '').includes('401')) {
          setError('Login required to view watchlist stories');
        } else {
          setError('Failed to load stories');
        }
        setLoading(false);
      });

    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistOnly, backendSearch, autoSearchScope, storiesDate, storiesSinceIso, isRangeActive, dateRangeValue && dateRangeValue.start, dateRangeValue && dateRangeValue.end, pinnedStoryId]);

  const baseStories = useMemo(() => {
    let list = Array.isArray(stories) ? [...stories] : [];

    return list;
  }, [stories]);

  const countsByType = useMemo(() => {
    const counts = {};
    (baseStories || []).forEach((s) => {
      const t = (s && s.story_type) ? String(s.story_type) : '';
      if (!t) return;
      counts[t] = (counts[t] || 0) + 1;

      if (Number(s && s.derived_count) > 0) {
        counts.__deep_dives_total = (counts.__deep_dives_total || 0) + 1;
      }
    });
    return counts;
  }, [baseStories]);

  const sortedTypeCounts = useMemo(() => {
    return Object.entries(countsByType)
      .sort((a, b) => {
        const da = Number(a[1]) || 0;
        const db = Number(b[1]) || 0;
        if (db !== da) return db - da;
        return String(a[0]).localeCompare(String(b[0]));
      });
  }, [countsByType]);

  const mobileTypeFilterValue = useMemo(() => {
    return Array.isArray(selectedTypes) ? selectedTypes : [];
  }, [selectedTypes]);

  const filteredStories = useMemo(() => {
    let list = Array.isArray(baseStories) ? [...baseStories] : [];
    const pinnedSid = pinnedStoryId ? String(pinnedStoryId) : '';

    const normalizePrimaryToken = (v) => {
      if (!v) return '';
      const s = String(v).trim().toLowerCase();
      if (!s) return '';
      return s.includes('.') ? s.split('.')[0] : s;
    };

    if (Array.isArray(selectedTypes) && selectedTypes.length) {
      const set = new Set(selectedTypes);
      list = list.filter((s) => {
        const sid = s && s.id != null ? String(s.id) : '';
        if (pinnedSid && sid === pinnedSid) return true;
        return set.has((s && s.story_type) ? String(s.story_type) : '');
      });
    }

    if (onlyWithDeepDives) {
      list = list.filter((s) => {
        const sid = s && s.id != null ? String(s.id) : '';
        if (pinnedSid && sid === pinnedSid) return true;
        return Number(s && s.derived_count) > 0;
      });
    }

    // Collapse duplicates: same primary_token AND same story_type.
    // Keep the most recent story (highest ts_created).
    const byKey = new Map();
    list.forEach((s, idx) => {
      const primary = normalizePrimaryToken(s && s.primary_token);
      const type = (s && s.story_type) ? String(s.story_type) : '';
      if (!primary || !type) {
        // If either is missing, do not collapse.
        byKey.set(`__id__:${s && s.id ? s.id : idx}`, s);
        return;
      }
      const key = `${type}::${primary}`;
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, s);
        return;
      }

      const sid = s && s.id != null ? String(s.id) : '';
      const prevId = prev && prev.id != null ? String(prev.id) : '';
      if (pinnedSid && sid === pinnedSid) {
        byKey.set(key, s);
        return;
      }
      if (pinnedSid && prevId === pinnedSid) {
        return;
      }

      const prevTs = prev && prev.ts_created ? String(prev.ts_created) : '';
      const nextTs = s && s.ts_created ? String(s.ts_created) : '';
      if (nextTs.localeCompare(prevTs) > 0) {
        byKey.set(key, s);
      }
    });
    list = Array.from(byKey.values());

    const numericScores = list
      .map((s) => (typeof s.score === 'number' ? s.score : null))
      .filter((v) => v !== null);

    let min = Infinity;
    let max = -Infinity;
    numericScores.forEach((v) => {
      if (v < min) min = v;
      if (v > max) max = v;
    });

    const range = max > min ? max - min : 1;
    const normalize = (v) => 1 + ((v - min) / range) * 99;

    list = list.map((s) => {
      if (typeof s.score !== 'number' || !Number.isFinite(s.score)) return { ...s, normalizedScore: null };
      return { ...s, normalizedScore: normalize(s.score) };
    });

    if (sortMode === 'score') {
      list.sort((a, b) => {
        const sa = typeof a.normalizedScore === 'number' ? a.normalizedScore : -Infinity;
        const sb = typeof b.normalizedScore === 'number' ? b.normalizedScore : -Infinity;
        if (sb !== sa) return sb - sa;
        return (b.ts_created || '').localeCompare(a.ts_created || '');
      });
    } else {
      list.sort((a, b) => (b.ts_created || '').localeCompare(a.ts_created || ''));
    }

    const q = String(search || '').trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        if (!s) return false;
        const sid = s && s.id != null ? String(s.id) : '';
        if (pinnedSid && sid === pinnedSid) return true;
        const haystack = [
          s.story_type,
          s.primary_token,
          s.narrative,
          s.title,
          s.narrative_hint,
          s.meta && typeof s.meta === 'object' ? JSON.stringify(s.meta) : '',
        ].join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }

    return list;
  }, [baseStories, selectedTypes, sortMode, onlyWithDeepDives, search, pinnedStoryId]);

  const [manualStoryOrder, setManualStoryOrder] = useState([]);

  useEffect(() => {
    const ids = (filteredStories || [])
      .map((s) => (s && s.id != null ? String(s.id) : ''))
      .filter(Boolean);

    setManualStoryOrder((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const idSet = new Set(ids);
      const next = prevArr.filter((id) => idSet.has(String(id)));
      const seen = new Set(next.map((id) => String(id)));
      ids.forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        next.push(id);
      });
      return next;
    });
  }, [filteredStories]);

  useEffect(() => {
    setManualStoryOrder([]);
  }, [sortMode]);

  const orderedStories = useMemo(() => {
    const list = Array.isArray(filteredStories) ? filteredStories.slice() : [];
    const order = Array.isArray(manualStoryOrder) ? manualStoryOrder : [];
    if (!order.length) return list;
    const idxById = new Map(order.map((id, idx) => [String(id), idx]));
    return list.sort((a, b) => {
      const ia = idxById.has(String(a && a.id)) ? idxById.get(String(a && a.id)) : Number.MAX_SAFE_INTEGER;
      const ib = idxById.has(String(b && b.id)) ? idxById.get(String(b && b.id)) : Number.MAX_SAFE_INTEGER;
      if (ia !== ib) return ia - ib;
      return 0;
    });
  }, [filteredStories, manualStoryOrder]);

  // If stories were collapsed/filtered out, prune group selections that no longer exist.
  useEffect(() => {
    const ids = new Set((filteredStories || []).map((s) => s && s.id).filter(Boolean));

    setGroupedStoryIds((prev) => {
      const next = new Set();
      (prev || []).forEach((id) => {
        if (ids.has(id)) next.add(id);
      });

      if (groupModeEnabled && next.size === 0) {
        setGroupModeEnabled(false);
      }
      return next;
    });
  }, [filteredStories, groupModeEnabled]);

  const handleToggleType = useCallback((type) => {
    const t = type == null ? '' : String(type);
    if (!t) return;

    setSelectedTypes((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.includes(t)) return list.filter((x) => x !== t);
      return [...list, t];
    });
  }, []);

  const fetchDerivedStories = useCallback(async (sourceStoryId) => {
    const sid = sourceStoryId ? String(sourceStoryId) : '';
    if (!sid) return [];

    const qs = new URLSearchParams({ source_story_id: sid, limit: '50' }).toString();
    const resp = await fetch(`${SERVER_NAME}/stories/derived?${qs}`, { credentials: 'include' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchDerivedStoriesForRow = useCallback(async (sid) => {
    const id = sid ? String(sid) : '';
    if (!id) return;

    const derivedCache = derivedBySourceIdRef.current || {};
    const derivedLoading = derivedLoadingByIdRef.current || {};
    if (Array.isArray(derivedCache[id])) return;
    if (derivedLoading && derivedLoading[id]) return;

    setDerivedLoadingById((prev) => ({ ...(prev || {}), [id]: true }));
    setDerivedErrorById((prev) => ({ ...(prev || {}), [id]: '' }));
    try {
      const list = await fetchDerivedStories(id);
      setDerivedBySourceId((prev) => ({ ...(prev || {}), [id]: list }));
    } catch (err) {
      console.error('Error loading derived stories:', err);
      setDerivedBySourceId((prev) => ({ ...(prev || {}), [id]: [] }));
      setDerivedErrorById((prev) => ({ ...(prev || {}), [id]: 'Unable to load deep dives.' }));
    } finally {
      setDerivedLoadingById((prev) => ({ ...(prev || {}), [id]: false }));
    }
  }, [fetchDerivedStories, setDerivedBySourceId, setDerivedErrorById, setDerivedLoadingById]);

  const deepDiveColorFor = useCallback((questionType) => {
    const qt = questionType ? String(questionType).toUpperCase() : '';
    if (qt === 'TOP_COUNTERPARTY') return '#b45aff';
    if (qt === 'COUNTERPARTY_WALK') return '#00ffa0';
    return '#ff00ff';
  }, []);

  const selectedStory = useMemo(
    () => filteredStories.find((s) => s.id === selectedId) || filteredStories[0] || null,
    [filteredStories, selectedId]
  );

  useEffect(() => {
    const pinnedSid = pinnedStoryId ? String(pinnedStoryId) : '';
    if (!pinnedSid) {
      if (autoScrolledStoryId !== null) setAutoScrolledStoryId(null);
      return;
    }

    const sid = selectedId != null ? String(selectedId) : '';
    if (!sid || sid !== pinnedSid) return;
    if (autoScrolledStoryId === pinnedSid) return;

    const timer = setTimeout(() => {
      try {
        const el = document.getElementById(`story-item-${pinnedSid}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setAutoScrolledStoryId(pinnedSid);
        }
      } catch (e) {
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [pinnedStoryId, selectedId, storiesViewMode, filteredStories.length, autoScrolledStoryId]);

  useEffect(() => {
    const sid = selectedStory && selectedStory.id ? String(selectedStory.id) : '';
    if (!sid) return;

    const expected = Number(selectedStory && selectedStory.derived_count) || 0;
    const derivedCache = derivedBySourceIdRef.current || {};
    if (derivedCache && Array.isArray(derivedCache[sid])) {
      const cached = derivedCache[sid] || [];
      const has = cached.length > 0;
      setDerivedOpenById((prev) => ({ ...(prev || {}), [sid]: !!has }));

      // If server says there are deep dives but our cached list is stale (or
      // we previously cached empty), re-fetch so newly inserted deep dives
      // (e.g. depth=3 trails) show up without a hard reload.
      // Guard against loops when derived_count > limit (we fetch up to 50).
      const derivedLoading = derivedLoadingByIdRef.current || {};
      if (expected > cached.length && cached.length < 50 && !(derivedLoading && derivedLoading[sid])) {
        setDerivedLoadingById((prev) => ({ ...(prev || {}), [sid]: true }));
        setDerivedErrorById((prev) => ({ ...(prev || {}), [sid]: '' }));
        fetchDerivedStories(sid)
          .then((list) => {
            setDerivedBySourceId((prev) => ({ ...(prev || {}), [sid]: list }));
            const has2 = Array.isArray(list) && list.length > 0;
            setDerivedOpenById((prev) => ({ ...(prev || {}), [sid]: !!has2 }));
          })
          .catch((err) => {
            console.error('Error refreshing derived stories:', err);
          })
          .finally(() => {
            setDerivedLoadingById((prev) => ({ ...(prev || {}), [sid]: false }));
          });
      }

      return;
    }

    setDerivedLoadingById((prev) => ({ ...(prev || {}), [sid]: true }));
    setDerivedErrorById((prev) => ({ ...(prev || {}), [sid]: '' }));

    fetchDerivedStories(sid)
      .then((list) => {
        setDerivedBySourceId((prev) => ({ ...(prev || {}), [sid]: list }));
        const has = Array.isArray(list) && list.length > 0;
        setDerivedOpenById((prev) => ({ ...(prev || {}), [sid]: !!has }));
      })
      .catch((err) => {
        console.error('Error loading derived stories:', err);
        setDerivedBySourceId((prev) => ({ ...(prev || {}), [sid]: [] }));
        setDerivedOpenById((prev) => ({ ...(prev || {}), [sid]: false }));
      })
      .finally(() => {
        setDerivedLoadingById((prev) => ({ ...(prev || {}), [sid]: false }));
      });
  }, [selectedStory && selectedStory.id, selectedStory && selectedStory.derived_count, fetchDerivedStories]);

  const newWalletSeeds = useMemo(() => {
    if (!selectedStory || selectedStory.story_type !== 'NEW_WALLETS') return [];
    const meta = selectedStory.meta && typeof selectedStory.meta === 'object' ? selectedStory.meta : null;
    const raw =
      (meta && (meta.sample_addresses || meta.sample_addrs || meta.sample_wallets || meta.sample || meta.addresses)) ||
      [];

    const arr = Array.isArray(raw) ? raw : [];
    const out = [];
    const seen = new Set();
    for (const v of arr) {
      if (!v) continue;
      const s = String(v).trim().toLowerCase();
      const base = s.includes('.') ? s.split('.')[0] : s;
      if (!/^0x[0-9a-f]{40}$/.test(base)) continue;
      if (seen.has(base)) continue;
      seen.add(base);
      out.push(base);
      if (out.length >= 40) break;
    }
    return out;
  }, [selectedStory]);

  const hotlinksSeeds = useMemo(() => {
    if (!selectedStory || String(selectedStory.story_type || '') !== 'HOTLINKS') return null;
    const meta = selectedStory && selectedStory.meta && typeof selectedStory.meta === 'object' ? selectedStory.meta : null;
    const primary = (meta && meta.address) ? String(meta.address).trim() : (selectedStory.primary_token ? String(selectedStory.primary_token).trim() : '');
    if (!primary) return null;
    const sendersRaw = meta && Array.isArray(meta.sample_senders) ? meta.sample_senders : [];
    const senders = (sendersRaw || [])
      .map((s) => String(s || '').trim())
      .filter((s) => /^0x[0-9a-fA-F]{40}$/.test(s));
    const uniq = [];
    const seen = new Set();
    const push = (addr) => {
      const a = String(addr || '').toLowerCase();
      if (!a) return;
      if (seen.has(a)) return;
      seen.add(a);
      uniq.push(addr);
    };
    push(primary);
    senders.forEach((s) => push(s));

    // Keep bounded to protect Evidence Summary from huge fanouts.
    const MAX_SEEDS = 12;
    return uniq.slice(0, MAX_SEEDS);
  }, [selectedStory]);

  // Ensure we always have a concrete selectedId backing the currently
  // visible story, so the Evidence Summary is clearly associated with
  // a specific row. When the page first loads or filters change, default
  // to the first story in the filtered list.
  useEffect(() => {
    if (!filteredStories.length) {
      if (requestedStoryId) {
        if (selectedId !== requestedStoryId) setSelectedId(requestedStoryId);
        return;
      }

      if (selectedId !== null) setSelectedId(null);
      return;
    }

    if (requestedStoryId) {
      const target = filteredStories.find((s) => String(s && s.id) === String(requestedStoryId));
      if (target && target.id != null) {
        const tid = target.id;
        if (selectedId !== tid) setSelectedId(tid);
        setRequestedStoryId(null);
        return;
      }
    }

    const currentExists = filteredStories.some((s) => s.id === selectedId);
    if (!currentExists) {
      setSelectedId(filteredStories[0].id);
    }
  }, [filteredStories, selectedId, requestedStoryId]);

  // Whenever the selected story changes, reset the forensics primary token
  // back to that story's primary token and reset the selection history so the
  // back arrow can return to the original.
  useEffect(() => {
    if (selectedStory && selectedStory.primary_token) {
      const nextToken = selectedStory.primary_token;
      const nextChain = String(selectedStory.chain || 'ETH').toUpperCase();
      if (currentPrimaryToken !== nextToken) setCurrentPrimaryToken(nextToken);
      if (currentChain !== nextChain) setCurrentChain(nextChain);
      setSelectionHistory((prev) => (
        Array.isArray(prev) && prev.length === 1 && prev[0] === nextToken ? prev : [nextToken]
      ));
      if (selectionIndex !== 0) setSelectionIndex(0);
      if (expandedHistory) setExpandedHistory(false);
    } else {
      if (currentPrimaryToken !== null) setCurrentPrimaryToken(null);
      setSelectionHistory((prev) => (Array.isArray(prev) && prev.length === 0 ? prev : []));
      if (selectionIndex !== -1) setSelectionIndex(-1);
      if (expandedHistory) setExpandedHistory(false);
    }
  }, [selectedStory, currentPrimaryToken, currentChain, selectionIndex, expandedHistory]);

  const expandedHistorySeeds = useMemo(() => {
    if (!expandedHistory) return null;
    if (!Array.isArray(selectionHistory) || selectionHistory.length === 0) return null;
    if (selectionIndex < 0) return null;
    return selectionHistory.slice(0, selectionIndex + 1);
  }, [expandedHistory, selectionHistory, selectionIndex]);

  // If we have no current primary token (e.g. NEW_WALLETS stories), the
  // inline evidence pane is not rendered. Ensure any previous "Updating"
  // state is cleared so the header doesn't get stuck.
  useEffect(() => {
    if (!currentPrimaryToken) {
      setForensicsUpdating(false);
      setForensicsSnapshot(null);
    }
  }, [currentPrimaryToken]);

  const extractAddressesDeep = useCallback((x, out) => {
    if (!x) return;
    if (typeof x === 'string') {
      const m = x.match(/0x[0-9a-fA-F]{40}/g);
      if (m) {
        m.forEach((a) => out.add(String(a).toLowerCase()));
      }
      return;
    }
    if (Array.isArray(x)) {
      x.forEach((v) => extractAddressesDeep(v, out));
      return;
    }
    if (typeof x === 'object') {
      Object.keys(x).forEach((k) => extractAddressesDeep(x[k], out));
    }
  }, []);

  const storyAddresses = useMemo(() => {
    const map = new Map();
    (filteredStories || []).forEach((s) => {
      const out = new Set();
      if (s && s.primary_token) out.add(String(s.primary_token).toLowerCase());
      if (s && s.meta && typeof s.meta === 'object') extractAddressesDeep(s.meta, out);
      const body = getFullNarrative(s) || '';
      if (body) extractAddressesDeep(body, out);
      map.set(s.id, Array.from(out));
    });
    return map;
  }, [filteredStories, extractAddressesDeep]);

  const groupedSeeds = useMemo(() => {
    if (!groupedStoryIds || groupedStoryIds.size === 0) return [];
    const out = [];
    const seen = new Set();
    const norm = (v) => {
      if (!v) return '';
      const s = String(v).trim().toLowerCase();
      if (!s) return '';
      return s.includes('.') ? s.split('.')[0] : s;
    };

    groupedStoryIds.forEach((id) => {
      const s = (filteredStories || []).find((x) => x && x.id === id);
      if (!s) return;
      const token = norm((s && s.primary_token) || (s && s.meta && (s.meta.token || s.meta.token_address || s.meta.contract)));
      if (!token || token.length !== 42) return;
      if (seen.has(token)) return;
      seen.add(token);
      out.push(token);
    });

    return out;
  }, [groupedStoryIds, filteredStories]);

  const groupModeActive = !!groupModeEnabled && groupedSeeds.length > 0;

  const toggleGroupStory = useCallback((e, story) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!story || !story.id) return;

    setGroupedStoryIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(story.id)) next.delete(story.id);
      else next.add(story.id);
      return next;
    });
  }, []);

  // Enrich the primary token shown in the inline forensics panel with
  // metadata from the backend (EthNames + EthMeta via /api/addressMeta).
  useEffect(() => {
    const address = currentPrimaryToken;
    const normalizedAddress = (() => {
      try {
        if (!address) return '';
        const s = String(address).trim().toLowerCase();
        const base = s.includes('.') ? s.split('.')[0] : s;
        return base;
      } catch (e) {
        return '';
      }
    })();

    if (!normalizedAddress) {
      setPrimaryTokenMeta(null);
      setPrimaryTokenMetaLoading(false);
      return;
    }

    let cancelled = false;
    const fetchMeta = async () => {
      try {
        setPrimaryTokenMetaLoading(true);
        const resp = await fetch(`${SERVER_NAME}/addressMetaBatch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ addresses: [normalizedAddress] }),
        });
        if (!resp.ok) {
          if (!cancelled) {
            setPrimaryTokenMeta(null);
          }
          return;
        }
        const data = await resp.json();
        const rows = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.data)
            ? data.data
            : (data && data.data && Array.isArray(data.data.data)
              ? data.data.data
              : []));
        const row = rows && rows.length ? rows[0] : null;
        if (!cancelled) {
          setPrimaryTokenMeta(row || null);
        }
      } catch (e) {
        if (!cancelled) {
          setPrimaryTokenMeta(null);
        }
      } finally {
        if (!cancelled) {
          setPrimaryTokenMetaLoading(false);
        }
      }
    };

    fetchMeta();
    return () => {
      cancelled = true;
    };
  }, [currentPrimaryToken]);

  // Show a single unified list of the latest stories, capped at 100.
  const briefStories = useMemo(() => orderedStories.slice(0, 100), [orderedStories]);
  const moreStories = useMemo(() => [], []);

  const scrollStoryIntoView = useCallback((sid) => {
    const id = sid != null ? String(sid) : '';
    if (!id) return;
    try {
      requestAnimationFrame(() => {
        try {
          const el = document.getElementById(`story-item-${id}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (err) {
        }
      });
    } catch (err) {
    }
  }, []);

  const moveSelectedStory = useCallback((delta) => {
    const sid = selectedId != null ? String(selectedId) : '';
    if (!sid) return;

    setManualStoryOrder((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      const idx = arr.findIndex((id) => String(id) === sid);
      if (idx === -1) return arr;
      const nextIdx = idx + Number(delta || 0);
      if (nextIdx < 0 || nextIdx >= arr.length) return arr;
      const tmp = arr[idx];
      arr[idx] = arr[nextIdx];
      arr[nextIdx] = tmp;
      return arr;
    });

    scrollStoryIntoView(sid);
  }, [selectedId, scrollStoryIntoView]);

  const handleSelectAllGroupedStories = useCallback(() => {
    const ids = [...briefStories, ...moreStories]
      .map((s) => (s && s.id ? s.id : null))
      .filter(Boolean);
    setGroupedStoryIds(new Set(ids));
  }, [briefStories, moreStories]);

  const handleSelectStory = (story) => setSelectedId(story.id);

  const promotePrimaryToken = (addr) => {
    if (!addr || typeof addr !== 'string') return;
    if (addr === currentPrimaryToken) return;

    // Any new selection should exit expanded-history mode and return to the
    // single-step breadcrumb view.
    if (expandedHistory) setExpandedHistory(false);

    setSelectionHistory((prev) => {
      // Ensure the base history always starts with the story's primary token
      // when available.
      let base = prev;
      if (!base.length && selectedStory && selectedStory.primary_token) {
        base = [selectedStory.primary_token];
      }

      // If we've navigated back in history, drop any "forward" entries
      // before appending a new selection.
      let trimmed = base;
      if (selectionIndex >= 0 && selectionIndex < base.length - 1) {
        trimmed = base.slice(0, selectionIndex + 1);
      }

      // If the last entry already matches, just move the index there.
      if (trimmed[trimmed.length - 1] === addr) {
        setSelectionIndex(trimmed.length - 1);
        return trimmed;
      }

      const next = [...trimmed, addr];
      setSelectionIndex(next.length - 1);
      return next;
    });

    setCurrentPrimaryToken(addr);
  };

  const handleOpenInE3D = async () => {
    if (!selectedStory) return;
    const params = new URLSearchParams();
    params.set('storyId', selectedStory.id);

    if (selectedStory.story_type === 'CATEGORY') {
      const category = selectedStory.meta && selectedStory.meta.category;
      if (category) {
        params.set('category', category);
        try {
          const qs = new URLSearchParams({ category }).toString();
          const resp = await fetch(`${SERVER_NAME}/categoryTokensLastHour?${qs}`);
          if (resp.ok) {
            const data = await resp.json();
            if (typeof window !== 'undefined') window.prefetchedCategoryTokens = data;
          }
        } catch (err) {
          console.error('Error prefetching category tokens:', err);
        }
      }
      params.set('time_window', (selectedStory.meta && selectedStory.meta.time_window) || 'last_hour');
      navigate(`/token?${params.toString()}`);
      return;
    }

    const { address } = getLinksTarget(selectedStory);

    // When launching E3D from a story card (not the inline forensics), we
    // don't yet have an inline snapshot, so just navigate normally.
    if (!forensicsSnapshot || !forensicsSnapshot.token) {
      if (address) navigate(`/links/${encodeURIComponent(address)}?${params.toString()}`);
      else navigate(`/links?${params.toString()}`);
      return;
    }

    // If we do have a snapshot, prefer that as the entry point and hydrate
    // the full /links view with the exact same graph and token list.
    const target = forensicsSnapshot.token;
    if (typeof window !== 'undefined') {
      window.prefetchedForensics = {
        token: target,
        graphData: forensicsSnapshot.graphData || { nodes: [], links: [] },
        tokenList: forensicsSnapshot.tokenList || [],
        storyId: selectedStory.id,
      };
    }

    navigate(`/links/${encodeURIComponent(target)}?${params.toString()}`);
  };

  const handleEmbedStory = async () => {
    if (!selectedStory) return;

    if (!isAuthenticated) {
      showMembersOnlyDialog();
      return;
    }

    setEmbedDialogOpen(true);
    setEmbedLoading(true);
    setEmbedError('');

    try {
      const resp = await fetch(`${SERVER_NAME}/embed/mint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ storyId: selectedStory.id }),
      });

      if (!resp.ok) {
        if (resp.status === 401) {
          showMembersOnlyDialog();
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const rawSnippet = data && data.iframe ? String(data.iframe) : '';
      const rawEmbedUrl = data && data.embedUrl ? String(data.embedUrl) : '';
      const normalizeEmbedUrl = (url) => {
        if (!url) return '';
        const trimmed = String(url).trim();
        if (trimmed.startsWith('/')) return `https://e3d.ai${trimmed}`;
        try {
          const u = new URL(trimmed, 'https://e3d.ai');
          if (
            u.hostname === 'localhost' ||
            u.hostname === '127.0.0.1' ||
            u.hostname === '0.0.0.0'
          ) {
            u.protocol = 'https:';
            u.hostname = 'e3d.ai';
            u.port = '';
          }
          return u.toString();
        } catch (e) {
          return trimmed;
        }
      };

      const rawSrcMatch = rawSnippet ? rawSnippet.match(/\bsrc\s*=\s*['\"]([^'\"]+)['\"]/i) : null;
      const rawSrc = rawSrcMatch && rawSrcMatch[1] ? String(rawSrcMatch[1]) : '';
      const srcCandidate = rawEmbedUrl || rawSrc;
      const normalizedSrc = normalizeEmbedUrl(srcCandidate);
      const normalizedSnippet = rawSnippet
        ? rawSnippet.replace(/(\bsrc\s*=\s*['\"])([^'\"]+)(['\"])/i, `$1${normalizedSrc}$3`)
        : (normalizedSrc
            ? `<iframe src="${normalizedSrc}" width="420" height="720" style="border:0;border-radius:12px;overflow:hidden" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allow="clipboard-write"></iframe>`
            : '');

      // For local preview, avoid framing https://e3d.ai directly (it may send
      // frame restrictions). Instead, load the embed route from the current
      // origin, which will render StoryEmbedPage locally and fetch data from
      // /api/embed/story.
      let previewUrl = normalizedSrc;
      try {
        if (typeof window !== 'undefined' && window.location && window.location.origin) {
          const origin = window.location.origin;
          const host = window.location.hostname;
          const isNonProd = host !== 'e3d.ai';
          if (isNonProd && normalizedSrc) {
            const u = new URL(normalizedSrc);
            previewUrl = `${origin}${u.pathname}${u.search}`;
          }
        }
      } catch (e) {
        // ignore
      }

      setEmbedSnippet(normalizedSnippet);
      setEmbedPreviewSrc(previewUrl);
    } catch (err) {
      console.error('Error creating embed snippet:', err);
      setEmbedError('Unable to generate embed snippet.');
    } finally {
      setEmbedLoading(false);
    }
  };

  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedSnippet, setEmbedSnippet] = useState('');
  const [embedPreviewSrc, setEmbedPreviewSrc] = useState('');
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState('');
  const [embedCopied, setEmbedCopied] = useState(false);

  const handleCopyEmbedSnippet = async () => {
    const text = embedSnippet || '';
    if (!text) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setEmbedCopied(true);
        setTimeout(() => setEmbedCopied(false), 1200);
      }
    } catch (e) {
      // ignore clipboard failures
    }
  };

  const buildStoryBriefSection = (s, index) => {
    const storyType = String(s.story_type || 'STORY').toUpperCase();
    const score = typeof s.normalizedScore === 'number' ? Math.round(s.normalizedScore) : (typeof s.score === 'number' ? s.score : '');
    const primaryAddress = String(getLinksTarget(s).address || s.primary_token || '').trim();

    const narrative = getFullNarrative(s) || '';
    const bullets = parseNarrativeBullets(narrative);
    const hookLine     = bullets && bullets.find(b => /hook/i.test(b.label));
    const happenedLine = bullets && bullets.find(b => /happened/i.test(b.label));
    const mattersLine  = bullets && bullets.find(b => /matters/i.test(b.label));
    const receiptsLine = bullets && bullets.find(b => /receipt/i.test(b.label));
    const verifyLine   = bullets && bullets.find(b => /verify/i.test(b.label));

    const hook     = hookLine     ? hookLine.body     : (narrative || '');
    const happened = happenedLine ? happenedLine.body : '';
    const matters  = mattersLine  ? mattersLine.body  : '';
    const receipts = receiptsLine ? receiptsLine.body : '';
    const verify   = verifyLine   ? verifyLine.body   : '';

    const addrSet = storyAddresses && storyAddresses.get(s.id) ? storyAddresses.get(s.id) : [];
    const evidenceRows = addrSet.length
      ? addrSet.map((addr, i) => {
          const role = i === 0 ? 'Primary' : 'Related';
          return `| ${role} | ${addr} |  |`;
        }).join('\n')
      : '| — | — | — |';

    const sid = s.id ? String(s.id) : '';
    const deepDives = (sid && derivedBySourceId && Array.isArray(derivedBySourceId[sid]) && derivedBySourceId[sid].length)
      ? derivedBySourceId[sid].map((d, i) => {
          const dtype = d.question_type || d.story_type || 'DEEP_DIVE';
          const dtitle = d.title || `Deep dive ${i + 1}`;
          const dnarr = getFullNarrative(d) || d.subtitle || '';
          return `### ${dtype}: ${dtitle}\n${dnarr}`;
        }).join('\n\n')
      : '';

    const heading = index !== undefined ? `## Story ${index + 1}: ${storyType}` : `## Story`;

    const sidShort = sid.length > 8 ? `${sid.slice(0, 4)}…${sid.slice(-4)}` : sid;

    return [
      heading,
      `**Story Type:** ${storyType}`,
      score !== '' ? `**Score:** ${score}${sid ? `  |  **ID:** ${sidShort} (\`${sid}\`)` : ''}` : (sid ? `**ID:** ${sidShort} (\`${sid}\`)` : null),
      primaryAddress ? `**Primary Address:** ${primaryAddress}` : null,
      ``,
      `### Narrative`,
      `**Hook:** ${hook}`,
      happened ? `**What happened:** ${happened}` : null,
      matters  ? `**Why it matters:** ${matters}`  : null,
      receipts ? `**Receipts:** ${receipts}`        : null,
      verify   ? `**How to verify:** ${verify}`     : null,
      ``,
      `### Evidence Addresses`,
      `| Role | Address | Links |`,
      `|------|---------|-------|`,
      evidenceRows,
      ``,
      `### Deep Dives`,
      deepDives || '_None attached._',
      ``,
      `### Investigation Notes`,
      ``,
    ].filter(line => line !== null).join('\n');
  };

  const handleExportBrief = (storyOverride = null) => {
    const activeStory = storyOverride || selectedStory;
    if (!activeStory && !(groupModeActive && groupedStoryIds && groupedStoryIds.size > 0)) return;
    try {
      const generatedAt = new Date().toISOString();
      const dateStr = new Date().toISOString().slice(0, 10);
      let md, filename;

      if (!storyOverride && groupModeActive && groupedStoryIds && groupedStoryIds.size > 1) {
        const groupedStories = [];
        groupedStoryIds.forEach((id) => {
          const s = (filteredStories || []).find((x) => x && x.id === id);
          if (s) groupedStories.push(s);
        });

        const storyType = groupedStories[0] ? String(groupedStories[0].story_type || 'STORY').toUpperCase() : 'GROUP';
        filename = `E3D_Brief_GROUP_${storyType}_${dateStr}_${groupedStories.length}stories.md`;

        const sections = groupedStories.map((s, i) => buildStoryBriefSection(s, i)).join('\n---\n\n');

        md = [
          `# E3D Investigation Brief — Group (${groupedStories.length} Stories)`,
          `**Generated:** ${generatedAt}`,
          `**Stories:** ${groupedStories.length}`,
          ``,
          `---`,
          ``,
          sections,
          `---`,
          `*Generated by E3D On-Chain Intelligence — https://e3d.ai*`,
        ].join('\n');
      } else {
        const s = activeStory;
        const storyType = String(s.story_type || 'STORY').toUpperCase();
        const primaryAddress = String(getLinksTarget(s).address || s.primary_token || '').trim();
        const addrShort = primaryAddress ? primaryAddress.slice(0, 10) : 'unknown';
        const storyDateStr = s.ts_created ? new Date(s.ts_created).toISOString().slice(0, 10) : dateStr;
        filename = `E3D_Brief_${storyType}_${storyDateStr}_${addrShort}.md`;

        const score = typeof s.normalizedScore === 'number' ? Math.round(s.normalizedScore) : (typeof s.score === 'number' ? s.score : '');

        md = [
          `# E3D Investigation Brief`,
          `**Generated:** ${generatedAt}`,
          `**Story Type:** ${storyType}`,
          score !== '' ? `**Score:** ${score}` : null,
          primaryAddress ? `**Primary Address:** ${primaryAddress}` : null,
          ``,
          `---`,
          ``,
          buildStoryBriefSection(s),
          `---`,
          `*Generated by E3D On-Chain Intelligence — https://e3d.ai*`,
        ].filter(line => line !== null).join('\n');
      }

      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (e) {
      console.error('[ExportBrief] error:', e);
    }
  };

  const relativeTime = selectedStory ? formatRelativeTime(selectedStory.ts_created) : '';
  const fullNarrative = selectedStory ? getFullNarrative(selectedStory) : '';
  const narrativeBullets = parseNarrativeBullets(fullNarrative);
  const launchLabel = buildLaunchLabel(selectedStory);

  const rabbitMeta = useMemo(() => {
    if (!selectedStory) return null;

    // Prefer already-parsed meta if present
    if (selectedStory.meta && typeof selectedStory.meta === 'object') {
      return selectedStory.meta;
    }

    const rawMeta = selectedStory.meta_json || selectedStory.metaJson || null;
    if (!rawMeta || typeof rawMeta !== 'string') return null;
    const parsed = safeJsonParse(rawMeta);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  }, [selectedStory]);

  const groupedRabbitMetas = useMemo(() => {
    if (!groupModeActive || !groupedStoryIds || groupedStoryIds.size === 0) return null;

    const out = [];
    groupedStoryIds.forEach((id) => {
      const s = (filteredStories || []).find((x) => x && x.id === id);
      if (!s) return;
      const primary = s && s.primary_token ? String(s.primary_token) : '';
      if (!primary) return;

      let metaObj = null;
      if (s.meta && typeof s.meta === 'object') {
        metaObj = s.meta;
      } else {
        const rawMeta = s.meta_json || s.metaJson || null;
        if (rawMeta && typeof rawMeta === 'string') {
          const parsed = safeJsonParse(rawMeta);
          if (parsed && typeof parsed === 'object') metaObj = parsed;
        }
      }
      if (!metaObj) return;

      out.push({ token: primary, meta: metaObj });
    });

    return out.length ? out : null;
  }, [groupModeActive, groupedStoryIds, filteredStories]);

  const typeOptions = useMemo(() => {
    const set = new Set((stories || []).map((s) => s.story_type).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [stories]);

  const storiesTier = useMemo(() => {
    if (storiesTierHeader) return String(storiesTierHeader);
    if (!isAuthenticated) return 'non_subscriber';
    const r = Array.isArray(roles) ? roles : [];
    if (r.includes('enterprise')) return 'enterprise';
    if (r.includes('premium')) return 'premium';
    return 'free_subscriber';
  }, [storiesTierHeader, isAuthenticated, roles]);

  const storiesTierLabel = useMemo(() => {
    if (storiesTier === 'premium' || storiesTier === 'enterprise') return 'Premium';
    if (storiesTier === 'free_subscriber') return 'Subscriber';
    return 'Non-subscriber';
  }, [storiesTier]);

  const pumpSignals = useMemo(() => {
    const meta = rabbitMeta && typeof rabbitMeta === 'object' ? rabbitMeta : null;
    if (!meta) return [];

    const out = [];
    const seen = new Set();
    const push = (label, color, background) => {
      const key = String(label || '').toLowerCase();
      if (!label || seen.has(key)) return;
      seen.add(key);
      out.push({ label, color, background });
    };

    const severity = String(meta?.pump?.severity || '').toLowerCase();
    if (severity === 'extreme') push('Extreme pump', '#ff7a7a', 'rgba(255,122,122,0.12)');
    else if (severity === 'high') push('High pump', '#ffe082', 'rgba(255,224,130,0.12)');
    else if (severity === 'medium') push('Medium pump', '#9ef7c5', 'rgba(158,247,197,0.10)');

    const participation = String(meta?.participation_type || meta?.confirmation_quality || '').trim();
    if (participation) push(participation, '#9ea4d8', 'rgba(255,255,255,0.06)');

    const liquidityHint = String(meta?.pump?.liquidity_hint || '').trim();
    if (liquidityHint) push(liquidityHint, '#9ea4d8', 'rgba(255,255,255,0.06)');

    const tvl = Number(meta?.pump?.current_pool_tvl_usd || meta?.pool?.tvl_usd || meta?.pool?.tvlUSD || 0);
    if (tvl > 0) {
      const compactUsd = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n) || n <= 0) return '$0';
        if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
        if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
        return `$${n.toFixed(0)}`;
      };
      push(`TVL ${compactUsd(tvl)}`, '#cfd3ff', 'rgba(77,121,255,0.10)');
    }

    return out.slice(0, 5);
  }, [rabbitMeta]);

  const updatedLabel = useMemo(() => {
    const topTs = filteredStories[0]?.ts_created;
    const base = topTs ? `Updated ${formatRelativeTime(topTs)}` : 'Updated recently';
    const n = Array.isArray(filteredStories) ? filteredStories.length : 0;
    const countLabel = `${n} ${n === 1 ? 'story' : 'stories'}`;
    return `${base} \u2022 ${countLabel}`;
  }, [filteredStories]);

  const renderForensicsPanel = (options = {}) => {
    const { fullHeight = false } = options;

    return (
      <div
        className={classes.forensicsPanel}
        style={fullHeight ? { flex: 1, minHeight: 0 } : undefined}
      >
        <div className={classes.forensicsHeader}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className={classes.forensicsTitle}>Evidence Summary</div>
              {forensicsUpdating && (
                <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                  Updating evidence…
                </Typography>
              )}
            </div>
            <div className={classes.forensicsTokenLabel}>{groupModeActive ? 'Group seeds' : 'Primary token'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 6 }}>
              <IconButton
                size="small"
                onClick={() => setForensicsMode('both')}
                style={{
                  padding: 4,
                  color: forensicsMode === 'both' ? '#ffffff' : '#9ea4d8',
                  border: forensicsMode === 'both' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                  borderRadius: 8,
                }}
              >
                <ViewAgendaIcon style={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setForensicsMode('list')}
                style={{
                  padding: 4,
                  color: forensicsMode === 'list' ? '#ffffff' : '#9ea4d8',
                  border: forensicsMode === 'list' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                  borderRadius: 8,
                }}
              >
                <ListIcon style={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setForensicsMode('graph')}
                style={{
                  padding: 4,
                  color: forensicsMode === 'graph' ? '#ffffff' : '#9ea4d8',
                  border: forensicsMode === 'graph' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                  borderRadius: 8,
                }}
              >
                <TimelineIcon style={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setForensicsMode('tokeninfo')}
                style={{
                  padding: 4,
                  color: forensicsMode === 'tokeninfo' ? '#ffffff' : '#9ea4d8',
                  border: forensicsMode === 'tokeninfo' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                  borderRadius: 8,
                }}
              >
                <CodeIcon style={{ fontSize: 16 }} />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => {
                  try {
                    window.dispatchEvent(new Event('toggle-stories-graph-toolbar'));
                  } catch (err) {
                  }
                }}
                style={{
                  padding: 4,
                  color: '#9ea4d8',
                  border: '1px solid transparent',
                  borderRadius: 8,
                }}
              >
                <SettingsIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          <IconButton
            size="small"
            onClick={() => {
              setForensicsFullScreen((prev) => !prev);
            }}
            style={{ color: '#cfd3ff' }}
          >
            {forensicsFullScreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </div>

        {selectedStory && (currentPrimaryToken || newWalletSeeds.length > 0) ? (
          <>
            {currentPrimaryToken ? (
              <>
              <div className={classes.forensicsTokenValue}>
                {/* History navigation arrows for promoted primaries */}
                <IconButton
                  size="small"
                  style={{ marginRight: 4, padding: 2, color: '#cfd3ff' }}
                  disabled={expandedHistory || selectionHistory.length === 0 || selectionIndex <= 0}
                  onClick={() => {
                    if (expandedHistory || selectionHistory.length === 0 || selectionIndex <= 0) return;
                    const nextIndex = selectionIndex - 1;
                    const addr = selectionHistory[nextIndex];
                    if (!addr) return;
                    setSelectionIndex(nextIndex);
                    setCurrentPrimaryToken(addr);
                  }}
                >
                  <ChevronLeftIcon style={{ fontSize: 20 }} />
                </IconButton>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!selectionHistory.length || selectionIndex < 0) return;
                    setExpandedHistory((prev) => !prev);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (!selectionHistory.length || selectionIndex < 0) return;
                      setExpandedHistory((prev) => !prev);
                    }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 10,
                    height: 18,
                    marginRight: 4,
                    padding: '0 6px',
                    borderRadius: 8,
                    color: '#cfd3ff',
                    background: expandedHistory ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)',
                    border: expandedHistory ? '1px solid rgba(158, 196, 255, 0.35)' : '1px solid rgba(158, 196, 255, 0.22)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: 12,
                    lineHeight: '18px',
                  }}
                  title={expandedHistory ? 'Collapse history' : 'Expand history'}
                >
                  {expandedHistory ? 'X' : String(selectionIndex + 1)}
                </span>
                <IconButton
                  size="small"
                  style={{ marginRight: 4, padding: 2, color: '#cfd3ff' }}
                  disabled={
                    expandedHistory ||
                    selectionHistory.length === 0 ||
                    selectionIndex === -1 ||
                    selectionIndex >= selectionHistory.length - 1
                  }
                  onClick={() => {
                    if (
                      expandedHistory ||
                      selectionHistory.length === 0 ||
                      selectionIndex === -1 ||
                      selectionIndex >= selectionHistory.length - 1
                    ) return;
                    const nextIndex = selectionIndex + 1;
                    const addr = selectionHistory[nextIndex];
                    if (!addr) return;
                    setSelectionIndex(nextIndex);
                    setCurrentPrimaryToken(addr);
                  }}
                >
                  <ChevronRightIcon style={{ fontSize: 20 }} />
                </IconButton>
                {primaryTokenMeta && (
                  <>
                    {primaryTokenMeta.icon && (
                      <img
                        src={primaryTokenMeta.icon}
                        alt=""
                        style={{ width: 18, height: 18, borderRadius: 4, marginRight: 6, verticalAlign: 'middle' }}
                      />
                    )}
                    {(primaryTokenMeta.name || primaryTokenMeta.symbol) && (
                      <span style={{ marginRight: 8 }}>
                        {primaryTokenMeta.name || primaryTokenMeta.symbol}
                      </span>
                    )}
                  </>
                )}
                <span>{shortenAddress(currentPrimaryToken)}</span>
                {pumpSignals.length > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginLeft: 10 }}>
                    {pumpSignals.map((chip) => (
                      <Chip
                        key={chip.label}
                        size="small"
                        label={chip.label}
                        style={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 800,
                          color: chip.color,
                          backgroundColor: chip.background,
                          border: `1px solid ${chip.color}33`,
                        }}
                      />
                    ))}
                  </span>
                )}
                <IconButton
                  size="small"
                  style={{ marginLeft: 6, padding: 2, color: '#cfd3ff' }}
                  onClick={() => {
                    if (!selectedStory || !currentPrimaryToken) return;

                    // If the inline forensics pane has a snapshot matching the
                    // current primary token, stash it globally so /links can
                    // hydrate immediately with the same graph.
                    if (
                      forensicsSnapshot &&
                      forensicsSnapshot.token &&
                      forensicsSnapshot.token.toLowerCase() === currentPrimaryToken.toLowerCase()
                    ) {
                      if (typeof window !== 'undefined') {
                        window.prefetchedForensics = {
                          token: forensicsSnapshot.token,
                          graphData: forensicsSnapshot.graphData || { nodes: [], links: [] },
                          tokenList: forensicsSnapshot.tokenList || [],
                          storyId: selectedStory.id,
                        };
                      }
                    }

                    const params = new URLSearchParams();
                    params.set('storyId', selectedStory.id);
                    navigate(`/links/${encodeURIComponent(currentPrimaryToken)}?${params.toString()}`);
                  }}
                >
                  <OpenInNewIcon style={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  style={{ marginLeft: 6, padding: 2, color: '#cfd3ff' }}
                  onClick={() => {
                    if (!selectedStory || !currentPrimaryToken) return;
                    try {
                      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(currentPrimaryToken).catch(() => {});
                      }
                    } catch (e) {
                      // ignore clipboard errors
                    }
                  }}
                >
                  <FileCopyIcon style={{ fontSize: 14 }} />
                </IconButton>
              </div>

              <div className={classes.controlGroup}>
                <div className={classes.controlLabel}>Time</div>
                <TextField
                  className={classes.controlField}
                  variant="outlined"
                  size="small"
                  label=""
                  select
                  value={storiesTimeRange}
                  onChange={(e) => setStoriesTimeRange(String(e.target.value || '24h'))}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="24h">24 Hours</MenuItem>
                  <MenuItem value="12h">12 Hours</MenuItem>
                  <MenuItem value="1h">1 Hour</MenuItem>
                </TextField>
              </div>
              </>
            ) : (
              <div className={classes.forensicsTokenValue}>
                <span>{`Sample wallets (${newWalletSeeds.length})`}</span>
              </div>
            )}
            {forensicsMode === 'tokeninfo' ? (
              <div className={classes.tokenInfoPanel}>
                {!currentPrimaryToken ? (
                  <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                    Select a story with a primary token to view token details.
                  </Typography>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      {!!tokenInfoSource && (
                        <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                          {tokenInfoSource === 'coingecko' ? 'CoinGecko' : tokenInfoSource === 'onchain' ? 'On-chain' : tokenInfoSource}
                        </Typography>
                      )}
                      {tokenInfoCopied && (
                        <Typography variant="caption" style={{ color: '#9ef7c5', fontSize: 11 }}>
                          Copied
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        style={{ padding: 2, color: '#cfd3ff' }}
                        title="Copy token JSON"
                        onClick={() => {
                          const text = tokenInfoText || '';
                          if (!text) return;
                          try {
                            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(text).then(
                                () => {
                                  setTokenInfoCopied(true);
                                  setTimeout(() => setTokenInfoCopied(false), 1200);
                                },
                                () => {}
                              );
                            }
                          } catch (e) {
                            // ignore
                          }
                        }}
                      >
                        <FileCopyIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </div>
                    {tokenInfoLoading && (
                      <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11, marginBottom: 8, display: 'block' }}>
                        Loading token details…
                      </Typography>
                    )}
                    {tokenInfoError && (
                      <Typography variant="caption" style={{ color: '#ffb4b4', fontSize: 11, marginBottom: 8, display: 'block' }}>
                        {tokenInfoError}
                      </Typography>
                    )}
                    {tokenInfoText ? (
                      <pre className={classes.tokenInfoPre}>{tokenInfoText}</pre>
                    ) : (
                      !tokenInfoLoading && (
                        <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                          No token details found.
                        </Typography>
                      )
                    )}
                  </>
                )}
              </div>
            ) : (
              <StoriesLinksPane
                token={(groupModeActive
                  ? (groupedSeeds[0] || currentPrimaryToken)
                  : (currentPrimaryToken || newWalletSeeds[0] || null))}
                tokens={(
                  groupModeActive
                    ? groupedSeeds
                    : (
                      (hotlinksSeeds && hotlinksSeeds.length)
                        ? hotlinksSeeds
                        : (expandedHistorySeeds || (newWalletSeeds.length ? newWalletSeeds : null))
                    )
                )}
                seedCacheKey={(
                  (hotlinksSeeds && hotlinksSeeds.length)
                    ? `hotlinks:${String(selectedStory && selectedStory.id ? selectedStory.id : currentPrimaryToken || '')}:${hotlinksSeeds.map((x) => String(x || '').toLowerCase()).join(',')}`
                    : undefined
                )}
                seedKind={(() => {
                  const st = selectedStory && selectedStory.story_type ? String(selectedStory.story_type) : '';
                  if (st === 'WHALE' || st === 'NEW_WALLETS' || st === 'CLUSTER') return 'address';

                  // HOTLINKS stories are discovered from raw tx activity to an
                  // address (EthTransactions), so addressCounterparties is the
                  // right evidence source.
                  if (st === 'HOTLINKS') return 'address';

                  // SANDWICH: primary_token is the sandwiched token contract;
                  // use token evidence graph to show its swap activity.
                  if (st === 'SANDWICH') return 'token';

                  return 'token';
                })()}
                primaryTokenMeta={groupModeActive ? null : primaryTokenMeta}
                deepDives={(
                  (!groupModeActive && selectedStory && selectedStory.id && derivedBySourceId && Array.isArray(derivedBySourceId[String(selectedStory.id)]))
                    ? derivedBySourceId[String(selectedStory.id)]
                    : null
                )}
                showGraph={!isPrintMode && (forensicsMode === 'both' || forensicsMode === 'graph')}
                showList={isPrintMode || (forensicsMode === 'both' || forensicsMode === 'list')}
                isMobile={isMobile}
                onPromotePrimary={(selectedStory && selectedStory.story_type === 'NEW_WALLETS') ? null : promotePrimaryToken}
                rabbitMeta={
                  groupModeActive
                    ? groupedRabbitMetas
                    : (
                      rabbitMeta &&
                      selectedStory &&
                      selectedStory.primary_token &&
                      currentPrimaryToken &&
                      selectedStory.primary_token.toLowerCase() === currentPrimaryToken.toLowerCase()
                        ? rabbitMeta
                        : null
                    )
                }
                sideBySide={false}
                forceShowList={false}
              />
            )}
          </>
        ) : (
          <div className={classes.forensicsEmpty}>
            Select a story with a primary token to open its full links view.
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="storiesContainer" className={classes.root}>
      <Dialog
        open={embedDialogOpen}
        onClose={() => setEmbedDialogOpen(false)}
        fullWidth
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{
          className: 'embedPreviewDialogPaper',
          style: {
            backgroundColor: '#0b0e1e',
            color: '#ffffff',
            border: '1px solid rgba(77,121,255,0.55)',
            margin: isMobile ? 0 : 12,
            width: isMobile ? '100%' : '52vw',
            maxWidth: isMobile ? '100%' : 'min(920px, 52vw)',
            height: isMobile ? '100%' : '58vh',
            maxHeight: isMobile ? '100%' : 'min(740px, 58vh)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle style={{ color: '#ffffff', paddingRight: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>Embed Preview</span>
            <IconButton
              onClick={() => setEmbedDialogOpen(false)}
              title="Close"
              style={{ color: '#ffffff' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="embedPreviewDialogContent" style={{ color: '#ffffff', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              value={embedSnippet}
              onChange={(e) => setEmbedSnippet(e.target.value)}
              placeholder="Embed snippet will appear here"
              InputProps={{
                style: {
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#ffffff',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                },
              }}
            />
            <IconButton
              onClick={handleCopyEmbedSnippet}
              title={embedCopied ? 'Copied' : 'Copy'}
              style={{ color: '#ffffff' }}
            >
              <FileCopyIcon />
            </IconButton>
          </div>

          {embedLoading && (
            <div style={{ padding: 16 }}>
              <CircularProgress size={20} />
            </div>
          )}

          {!embedLoading && !!embedError && (
            <Typography variant="body2" style={{ color: '#f44336', padding: '8px 0' }}>
              {embedError}
            </Typography>
          )}

          {!embedLoading && !embedError && !!embedPreviewSrc && (
            <div
              style={{
                width: '100%',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#0b0e1e',
                flex: 1,
                minHeight: 0,
              }}
            >
              <iframe
                title="Embed preview"
                src={embedPreviewSrc}
                style={{ width: '100%', height: '100%', border: 'none', background: '#0b0e1e' }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className={classes.hero}>
        {/* DESKTOP: hero with meta and browse link only */}
        {!isMobile && (
          <div className={classes.heroTopRow}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div className={classes.heroTitle}>Today’s On-Chain Stories</div>
              <Button
                variant="contained"
                className={classes.primaryButton}
                onClick={() => navigate('/story-guide')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Guide
              </Button>
              <div className={classes.heroSubtitle} style={{ flexBasis: '100%' }}>
                High-signal pattern detection across chains, tokens, and wallets — each backed by E3D evidence.
              </div>

              {!isAuthenticated && (
                <div
                  style={{
                    flexBasis: '100%',
                    marginTop: 10,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'rgba(77, 121, 255, 0.14)',
                    border: '1px solid rgba(77, 121, 255, 0.35)',
                    color: '#cfd3ff',
                    fontSize: 12,
                    lineHeight: 1.35,
                  }}
                >
                  New stories are available with subscription.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Controls: desktop row vs mobile stacked.
            Treat mobile landscape like desktop so all controls share one row. */}
        {(!isMobile || isMobileLandscape) ? (
          <div className={classes.controlsRow}>
            <TextField
              id="storiesSearchField"
              className={`${classes.controlField} ${classes.controlFieldSearch}`}
              variant="outlined"
              size="small"
              label=""
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              onKeyDown={(e) => { if (e.key === 'Enter') setBackendSearch(search.trim()); }}
              InputProps={search ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearch(''); setBackendSearch(''); }} style={{ padding: 2, color: '#aaa' }} title="Clear search">
                      <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
            />

            <TextField
              className={classes.controlField}
              variant="outlined"
              size="small"
              label="Story Type"
              InputLabelProps={{ shrink: true, style: { whiteSpace: 'nowrap' } }}
              select
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const list = Array.isArray(selected) ? selected : [];
                  if (!list.length) return 'ALL';
                  return 'FILTERED';
                },
                MenuProps: {
                  PaperProps: {
                    className: classes.menuPaper,
                  },
                  MenuListProps: {
                    dense: true,
                    style: { paddingTop: 4, paddingBottom: 4 },
                  },
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                },
              }}
              value={mobileTypeFilterValue}
              onChange={(e) => {
                const raw = e && e.target ? e.target.value : [];
                const list = Array.isArray(raw) ? raw.map((x) => String(x)) : [];

                // Pseudo-type entry that toggles the derived-count filter.
                if (list.includes('__DEEP_DIVES__')) {
                  setOnlyWithDeepDives((prev) => !prev);
                }

                if (!list.length || list.includes('ALL')) {
                  setSelectedTypes([]);
                  return;
                }
                setSelectedTypes(Array.from(new Set(list)).filter((x) => x && x !== 'ALL' && x !== '__DEEP_DIVES__'));
              }}
            >
              <MenuItem value="ALL" dense className={classes.menuItemDense}>
                <Checkbox
                  checked={!Array.isArray(selectedTypes) || selectedTypes.length === 0}
                  style={{ padding: 0, marginRight: 8, color: '#9ef7c5' }}
                  size="small"
                />
                <ListItemText
                  primaryTypographyProps={{ className: classes.menuItemTextDense }}
                  primary="ALL"
                />
              </MenuItem>
              <MenuItem value="__DEEP_DIVES__" dense className={classes.menuItemDense}>
                <Checkbox
                  checked={!!onlyWithDeepDives}
                  style={{ padding: 0, marginRight: 8, color: '#ffb86b' }}
                  size="small"
                />
                <ListItemText
                  primaryTypographyProps={{ className: classes.menuItemTextDense }}
                  primary={`Deep Dives (${Number(countsByType && countsByType.__deep_dives_total) || 0})`}
                />
              </MenuItem>
              {typeOptions.map((t) => {
                const count = Number(countsByType && countsByType[t]) || 0;
                const checked = Array.isArray(selectedTypes) && selectedTypes.includes(t);
                return (
                  <MenuItem key={t} value={t} dense className={classes.menuItemDense}>
                    <Checkbox
                      checked={checked}
                      style={{ padding: 0, marginRight: 8, color: '#4d79ff' }}
                      size="small"
                    />
                    <ListItemText
                      primaryTypographyProps={{ className: classes.menuItemTextDense }}
                      primary={`${t} (${count})`}
                    />
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              className={classes.controlField}
              variant="outlined"
              size="small"
              label="Sort"
              select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
            >
              <MenuItem value="score">Score</MenuItem>
              <MenuItem value="time">Time</MenuItem>
            </TextField>

            <TextField
              className={classes.controlField}
              variant="outlined"
              size="small"
              label="Time"
              select
              value={storiesTimeRange}
              onChange={(e) => setStoriesTimeRange(String(e.target.value || '24h'))}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="24h">24 Hours</MenuItem>
              <MenuItem value="12h">12 Hours</MenuItem>
              <MenuItem value="1h">1 Hour</MenuItem>
            </TextField>

            <Button
              variant={onlyWithDeepDives ? 'contained' : 'outlined'}
              className={onlyWithDeepDives ? classes.primaryButton : undefined}
              onClick={() => setOnlyWithDeepDives((prev) => !prev)}
              style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Only deep dives
            </Button>

            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={handleOpenInE3D}
              disabled={!selectedStory}
              style={{ whiteSpace: 'nowrap' }}
            >
              Open
            </Button>

            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={handleEmbedStory}
              disabled={!selectedStory}
              style={{ whiteSpace: 'nowrap' }}
            >
              Embed
            </Button>

            <Button
              variant="contained"
              className={classes.primaryButton}
              disabled={!selectedStory}
              onClick={handleExportBrief}
              title="Download investigation brief as Markdown"
              style={{ minWidth: 64 }}
            >
              <GetAppIcon style={{ fontSize: 18 }} />
            </Button>

            <IconButton
              onClick={handleToggleWatchlistOnly}
              onMouseDown={handleToggleWatchlistOnly}
              onTouchStart={handleToggleWatchlistOnly}
              title={watchlistOnly ? 'Watchlist stories: ON' : 'Watchlist stories: OFF'}
              style={{ color: watchlistOnly ? '#ffd700' : '#cfd3ff', pointerEvents: 'auto', position: 'relative', zIndex: 50 }}
            >
              <FaStar />
              {watchedKeys && watchedKeys.size > 0 && (
                <span style={{ marginLeft: 6, fontSize: 12, color: '#fff' }}>
                  {watchedKeys.size}
                </span>
              )}
            </IconButton>

            <IconButton
              size="small"
              title={isRangeActive ? 'Date range: ON' : 'Date range'}
              onClick={(e) => {
                if (!isAuthenticated) {
                  setSignInAnchorEl(e.currentTarget);
                  return;
                }
                setDateRangeAnchorEl(e.currentTarget);
              }}
              style={{
                padding: 6,
                color: isRangeActive ? '#ffffff' : '#cfd3ff',
                border: isRangeActive ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                borderRadius: 10,
              }}
            >
              <DateRangeIcon style={{ fontSize: 18 }} />
            </IconButton>

            <Popover
              open={!!signInAnchorEl}
              anchorEl={signInAnchorEl}
              onClose={() => setSignInAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                style: {
                  background: 'rgba(10,14,30,0.98)',
                  border: '1px solid rgba(77, 121, 255, 0.35)',
                  borderRadius: 12,
                  padding: 10,
                  color: '#e5e7ff',
                  maxWidth: 280,
                },
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, color: '#cfd3ff', lineHeight: 1.35 }}>
                  Sign in to search historical data
                </div>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setSignInAnchorEl(null);
                    showLoginDialog();
                  }}
                  style={{ textTransform: 'none' }}
                >
                  Sign in →
                </Button>
              </div>
            </Popover>

            <DateRangePickerPopover
              open={!!dateRangeAnchorEl}
              anchorEl={dateRangeAnchorEl}
              onClose={() => setDateRangeAnchorEl(null)}
              maxLookbackDays={effectiveMaxLookbackDays}
              maxRangeMinutes={1440}
              value={dateRangeValue}
              onApply={(range) => {
                setRangeUpgradeMsg('');
                setDateRangeValue(range);
              }}
              onClear={() => {
                setRangeUpgradeMsg('');
                setDateRangeValue(null);
              }}
              disableLast30={!isPremiumPlus}
              onAttemptLast30={() => {
                if (!isPremiumPlus) {
                  setRangeUpgradeMsg('Upgrade to Premium for 30-day lookback');
                }
              }}
              upgradeMessage={rangeUpgradeMsg}
              onDismissUpgradeMessage={() => setRangeUpgradeMsg('')}
            />
          </div>
        ) : (
          <div className={classes.mobileControls}>
            <div className={classes.mobileSearchRow}>
              <div className={classes.mobileSearchInner}>
                <IconButton
                  onClick={() => navigate('/home')}
                  style={{ padding: 0, marginRight: 2 }}
                  size="small"
                  title="E3D Home"
                >
                  <img src={futLogoIcon200} style={{ width: 28, height: 28 }} alt="E3D" />
                </IconButton>
                <TextField
                  id="storiesSearchField"
                  className={`${classes.controlField} ${classes.controlFieldSearch}`}
                  variant="outlined"
                  size="small"
                  label=""
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  onKeyDown={(e) => { if (e.key === 'Enter') setBackendSearch(search.trim()); }}
                  InputProps={search ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { setSearch(''); setBackendSearch(''); }} style={{ padding: 2, color: '#aaa' }} title="Clear search">
                          <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
                        </IconButton>
                      </InputAdornment>
                    ),
                  } : undefined}
                />
                <Button
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={() => navigate('/story-guide')}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Guide
                </Button>
                <Button
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleOpenInE3D}
                  disabled={!selectedStory}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Open
                </Button>

                <Button
                  variant="contained"
                  className={classes.primaryButton}
                  disabled={!selectedStory}
                  onClick={handleExportBrief}
                  title="Download investigation brief as Markdown"
                  style={{ minWidth: 64 }}
                >
                  <GetAppIcon style={{ fontSize: 18 }} />
                </Button>

                <Button
                  variant={onlyWithDeepDives ? 'contained' : 'outlined'}
                  className={onlyWithDeepDives ? classes.primaryButton : undefined}
                  onClick={() => setOnlyWithDeepDives((prev) => !prev)}
                  style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                >
                  Only deep dives
                </Button>

                <IconButton
                  onClick={handleToggleWatchlistOnly}
                  onMouseDown={handleToggleWatchlistOnly}
                  onTouchStart={handleToggleWatchlistOnly}
                  title={watchlistOnly ? 'Watchlist stories: ON' : 'Watchlist stories: OFF'}
                  style={{ color: watchlistOnly ? '#ffd700' : '#cfd3ff', pointerEvents: 'auto', position: 'relative', zIndex: 50 }}
                >
                  <FaStar />
                  {watchedKeys && watchedKeys.size > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 12, color: '#fff' }}>
                      {watchedKeys.size}
                    </span>
                  )}
                </IconButton>

                <IconButton
                  size="small"
                  title={isRangeActive ? 'Date range: ON' : 'Date range'}
                  onClick={(e) => {
                    if (!isAuthenticated) {
                      setSignInAnchorEl(e.currentTarget);
                      return;
                    }
                    setDateRangeAnchorEl(e.currentTarget);
                  }}
                  style={{
                    padding: 6,
                    color: isRangeActive ? '#ffffff' : '#cfd3ff',
                    border: isRangeActive ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent',
                    borderRadius: 10,
                  }}
                >
                  <DateRangeIcon style={{ fontSize: 18 }} />
                </IconButton>

                <Popover
                  open={!!signInAnchorEl}
                  anchorEl={signInAnchorEl}
                  onClose={() => setSignInAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    style: {
                      background: 'rgba(10,14,30,0.98)',
                      border: '1px solid rgba(77, 121, 255, 0.35)',
                      borderRadius: 12,
                      padding: 10,
                      color: '#e5e7ff',
                      maxWidth: 280,
                    },
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, color: '#cfd3ff', lineHeight: 1.35 }}>
                      Sign in to search historical data
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setSignInAnchorEl(null);
                        showLoginDialog();
                      }}
                      style={{ textTransform: 'none' }}
                    >
                      Sign in →
                    </Button>
                  </div>
                </Popover>

                <DateRangePickerPopover
                  open={!!dateRangeAnchorEl}
                  anchorEl={dateRangeAnchorEl}
                  onClose={() => setDateRangeAnchorEl(null)}
                  maxLookbackDays={effectiveMaxLookbackDays}
                  maxRangeMinutes={1440}
                  value={dateRangeValue}
                  onApply={(range) => {
                    setRangeUpgradeMsg('');
                    setDateRangeValue(range);
                  }}
                  onClear={() => {
                    setRangeUpgradeMsg('');
                    setDateRangeValue(null);
                  }}
                  disableLast30={!isPremiumPlus}
                  onAttemptLast30={() => {
                    if (!isPremiumPlus) {
                      setRangeUpgradeMsg('Upgrade to Premium for 30-day lookback');
                    }
                  }}
                  upgradeMessage={rangeUpgradeMsg}
                  onDismissUpgradeMessage={() => setRangeUpgradeMsg('')}
                />
              </div>
            </div>

            <div className={classes.mobileFiltersRow}>
              <div className={classes.controlGroup}>
                <div className={classes.controlLabel}>Type</div>
                <TextField
                  className={classes.controlField}
                  variant="outlined"
                  size="small"
                  label=""
                  select
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const list = Array.isArray(selected) ? selected : [];
                      if (!list.length) return 'ALL';
                      return 'FILTERED';
                    },
                    MenuProps: {
                      PaperProps: {
                        className: classes.menuPaper,
                      },
                      MenuListProps: {
                        dense: true,
                        style: { paddingTop: 4, paddingBottom: 4 },
                      },
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                    },
                  }}
                  value={mobileTypeFilterValue}
                  onChange={(e) => {
                    const raw = e && e.target ? e.target.value : [];
                    const list = Array.isArray(raw) ? raw.map((x) => String(x)) : [];

                    // Pseudo-type entry that toggles the derived-count filter.
                    if (list.includes('__DEEP_DIVES__')) {
                      setOnlyWithDeepDives((prev) => !prev);
                    }

                    if (!list.length || list.includes('ALL')) {
                      setSelectedTypes([]);
                      return;
                    }
                    setSelectedTypes(Array.from(new Set(list)).filter((x) => x && x !== 'ALL' && x !== '__DEEP_DIVES__'));
                  }}
                >
                  <MenuItem value="ALL" dense className={classes.menuItemDense}>
                    <Checkbox
                      checked={!Array.isArray(selectedTypes) || selectedTypes.length === 0}
                      style={{ padding: 0, marginRight: 8, color: '#9ef7c5' }}
                      size="small"
                    />
                    <ListItemText
                      primaryTypographyProps={{ className: classes.menuItemTextDense }}
                      primary="ALL"
                    />
                  </MenuItem>
                  <MenuItem value="__DEEP_DIVES__" dense className={classes.menuItemDense}>
                    <Checkbox
                      checked={!!onlyWithDeepDives}
                      style={{ padding: 0, marginRight: 8, color: '#ffb86b' }}
                      size="small"
                    />
                    <ListItemText
                      primaryTypographyProps={{ className: classes.menuItemTextDense }}
                      primary={`Deep Dives (${Number(countsByType && countsByType.__deep_dives_total) || 0})`}
                    />
                  </MenuItem>
                  {typeOptions.map((t) => {
                    const count = Number(countsByType && countsByType[t]) || 0;
                    const checked = Array.isArray(selectedTypes) && selectedTypes.includes(t);
                    return (
                      <MenuItem key={t} value={t} dense className={classes.menuItemDense}>
                        <Checkbox
                          checked={checked}
                          style={{ padding: 0, marginRight: 8, color: '#4d79ff' }}
                          size="small"
                        />
                        <ListItemText
                          primaryTypographyProps={{ className: classes.menuItemTextDense }}
                          primary={`${t} (${count})`}
                        />
                      </MenuItem>
                    );
                  })}
                </TextField>
              </div>

              <div className={classes.controlGroup}>
                <div className={classes.controlLabel}>Sort</div>
                <TextField
                  className={classes.controlField}
                  variant="outlined"
                  size="small"
                  label=""
                  select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                >
                  <MenuItem value="score">Score</MenuItem>
                  <MenuItem value="time">Time</MenuItem>
                </TextField>
              </div>
            </div>
          </div>
        )}

        {isRangeActive && (
          <div className={classes.sectionHint} style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#cfd3ff' }}>📅 {formatRangeLabel(dateRangeValue.start, dateRangeValue.end)}</span>
            <IconButton
              size="small"
              title="Clear date range"
              onClick={() => setDateRangeValue(null)}
              style={{ padding: 2, color: '#cfd3ff' }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
            </IconButton>
          </div>
        )}

        <div className={classes.sectionHint} style={{ marginTop: 8, lineHeight: 1.35 }}>
          Limits: Non-subscriber 1 story/type (60m delay). Subscriber 3 stories/type. Premium all stories.{" "}
          <span style={{ color: '#cfd3ff' }}>Your tier: {storiesTierLabel}</span>
          {storiesTierLabel !== 'Premium' && storiesTierLabel !== 'Enterprise' && (
            <span>
              {' — '}
              <a
                href="/signup"
                style={{ color: '#7eb3ff', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
              >
                Upgrade to Premium →
              </a>
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={22} />
          <Typography variant="body2" style={{ color: '#cfd3ff' }}>
            Loading brief…
          </Typography>
        </div>
      ) : error ? (
        <div className={classes.emptyState}>{error}</div>
      ) : !filteredStories.length ? (
        <div className={classes.emptyState}>No stories match your filters.</div>
      ) : (
        (storiesViewMode === 'table') ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div className={classes.sectionTitleRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className={classes.sectionTitle}>Stories</div>
                <div className={classes.sectionHint}>{updatedLabel}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconButton
                  size="small"
                  title="Card view"
                  onClick={() => setStoriesViewMode('cards')}
                  style={{
                    padding: 4,
                    color: storiesViewMode === 'cards' ? '#ffffff' : '#9ea4d8',
                    border: storiesViewMode === 'cards'
                      ? '1px solid rgba(158, 196, 255, 0.9)'
                      : '1px solid transparent',
                    borderRadius: 8,
                  }}
                >
                  <ViewAgendaIcon style={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  title="List view"
                  onClick={() => setStoriesViewMode('list')}
                  style={{
                    padding: 4,
                    color: storiesViewMode === 'list' ? '#ffffff' : '#9ea4d8',
                    border: storiesViewMode === 'list'
                      ? '1px solid rgba(158, 196, 255, 0.9)'
                      : '1px solid transparent',
                    borderRadius: 8,
                  }}
                >
                  <ViewListIcon style={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Table view"
                  onClick={() => setStoriesViewMode('table')}
                  style={{
                    padding: 4,
                    color: storiesViewMode === 'table' ? '#ffffff' : '#9ea4d8',
                    border: storiesViewMode === 'table'
                      ? '1px solid rgba(158, 196, 255, 0.9)'
                      : '1px solid transparent',
                    borderRadius: 8,
                  }}
                >
                  <TableChartIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>
            </div>

            <div className={classes.feedPanel} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {(() => {
                const getDeepDiveCount = (story) => {
                  const sid = story && story.id != null ? String(story.id) : '';
                  const cachedLen = (sid && derivedBySourceId && Array.isArray(derivedBySourceId[sid])) ? derivedBySourceId[sid].length : 0;
                  return cachedLen || (Number(story && story.derived_count) || 0);
                };

                const getPrimaryAddress = (story) => {
                  const target = getLinksTarget(story) || {};
                  return String((target.address || story.primary_token || '')).trim();
                };

                const getHookSentence = (story) => {
                  const narrative = getFullNarrative(story) || '';
                  const bullets = parseNarrativeBullets(narrative);
                  const hookLine = bullets && bullets.find((b) => /hook/i.test(b.label));
                  const raw = (hookLine && hookLine.body) ? hookLine.body : (story && story.title ? String(story.title) : narrative);
                  const s = String(raw || '').replace(/\s+/g, ' ').trim();
                  if (!s) return '';
                  const parts = s.split(/(?<=[.!?])\s+/).filter(Boolean);
                  return (parts && parts.length) ? parts[0].trim() : s;
                };

                const openLinksForStory = (story) => {
                  if (!story) return;
                  const params = new URLSearchParams();
                  params.set('storyId', story.id);
                  if (story.story_type === 'CATEGORY') {
                    const category = story.meta && story.meta.category;
                    if (category) params.set('category', category);
                    params.set('time_window', (story.meta && story.meta.time_window) || 'last_hour');
                    navigate(`/token?${params.toString()}`);
                    return;
                  }
                  const { address } = getLinksTarget(story);
                  if (address) navigate(`/links/${encodeURIComponent(address)}?${params.toString()}`);
                  else navigate(`/links?${params.toString()}`);
                };

                const findRelatedForStory = (story) => {
                  const addr = getPrimaryAddress(story);
                  if (!addr) return;
                  setSearch(addr);
                  try {
                    const el = document.getElementById('storiesSearchField');
                    if (el && typeof el.scrollIntoView === 'function') {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    const input = el ? el.querySelector('input') : null;
                    if (input && typeof input.focus === 'function') input.focus();
                  } catch (err) {
                  }
                };

                const exportBriefForStory = (story) => {
                  handleExportBrief(story);
                };

                const sortValue = (story, key) => {
                  if (!story) return '';
                  if (key === 'time') return story.ts_created ? String(story.ts_created) : '';
                  if (key === 'type') return story.story_type ? String(story.story_type) : '';
                  if (key === 'score') return (typeof story.normalizedScore === 'number') ? story.normalizedScore : (typeof story.score === 'number' ? story.score : -Infinity);
                  if (key === 'chain') return story.chain ? String(story.chain) : '';
                  if (key === 'deepDives') return getDeepDiveCount(story);
                  return '';
                };

                const rows = (() => {
                  const list = Array.isArray(filteredStories) ? [...filteredStories] : [];
                  const dir = tableSortDir === 'asc' ? 1 : -1;
                  list.sort((a, b) => {
                    const va = sortValue(a, tableSortKey);
                    const vb = sortValue(b, tableSortKey);
                    if (typeof va === 'number' && typeof vb === 'number') {
                      if (va === vb) return 0;
                      return (va < vb ? -1 : 1) * dir;
                    }
                    const sa = String(va || '');
                    const sb = String(vb || '');
                    if (sa === sb) return 0;
                    return (sa < sb ? -1 : 1) * dir;
                  });
                  return list;
                })();

                const toggleSort = (key) => {
                  if (tableSortKey === key) {
                    setTableSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                  } else {
                    setTableSortKey(key);
                    setTableSortDir(key === 'score' ? 'desc' : 'asc');
                  }
                };

                const sortArrow = (key) => {
                  if (tableSortKey !== key) return '';
                  return tableSortDir === 'asc' ? ' ▲' : ' ▼';
                };

                return (
                  <TableContainer style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Table stickyHeader size="small" style={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            onClick={() => toggleSort('time')}
                            style={{ width: 80, cursor: 'pointer', fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}
                          >
                            Time{sortArrow('time')}
                          </TableCell>
                          <TableCell
                            onClick={() => toggleSort('type')}
                            style={{ width: 120, cursor: 'pointer', fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}
                          >
                            Type{sortArrow('type')}
                          </TableCell>
                          <TableCell
                            onClick={() => toggleSort('score')}
                            style={{ width: 60, cursor: 'pointer', fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}
                          >
                            Score{sortArrow('score')}
                          </TableCell>
                          <TableCell
                            onClick={() => toggleSort('chain')}
                            style={{ width: 70, cursor: 'pointer', fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}
                          >
                            Chain{sortArrow('chain')}
                          </TableCell>
                          <TableCell style={{ width: 130, fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}>
                            Primary
                          </TableCell>
                          <TableCell style={{ fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}>
                            Title / Hook
                          </TableCell>
                          <TableCell
                            onClick={() => toggleSort('deepDives')}
                            style={{ width: 50, cursor: 'pointer', fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}
                          >
                            Dives{sortArrow('deepDives')}
                          </TableCell>
                          <TableCell style={{ width: 100, fontSize: 11, color: '#cfd3ff', background: 'rgba(10, 14, 30, 0.98)' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((story) => {
                          const sid = story && story.id != null ? String(story.id) : '';
                          const isExpanded = !!(sid && expandedTableStoryId === sid);
                          const primary = getPrimaryAddress(story);
                          const hook = getHookSentence(story);
                          const diveCount = getDeepDiveCount(story);
                          const score = (typeof story.normalizedScore === 'number') ? Math.round(story.normalizedScore) : (typeof story.score === 'number' ? Math.round(story.score) : '');
                          const chain = story.chain ? String(story.chain) : '';

                          const narrative = getFullNarrative(story) || '';
                          const bullets = parseNarrativeBullets(narrative);
                          const getBullet = (re) => {
                            const line = bullets && bullets.find((b) => re.test(String(b.label || '')));
                            return line && line.body ? String(line.body) : '';
                          };
                          const hookFull = getBullet(/hook/i) || narrative;
                          const happened = getBullet(/happened/i);
                          const matters = getBullet(/matters/i);
                          const receipts = getBullet(/receipt/i);

                          const addressList = storyAddresses && storyAddresses.get(story.id) ? (storyAddresses.get(story.id) || []) : [];

                          return (
                            <React.Fragment key={sid || Math.random()}>
                              <TableRow
                                id={`story-item-${sid}`}
                                data-story-id={sid}
                                hover
                                onClick={() => {
                                  if (!sid) return;
                                  setExpandedTableStoryId((prev) => (prev === sid ? null : sid));
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <TableCell style={{ width: 80, fontSize: 11, color: '#9ea4d8' }}>{formatRelativeTime(story.ts_created)}</TableCell>
                                <TableCell style={{ width: 120 }}>
                                  <Chip size="small" className={classes.chipType} label={story.story_type || 'STORY'} />
                                </TableCell>
                                <TableCell style={{ width: 60, fontSize: 12, color: '#e5e7ff', fontWeight: 700 }}>{score}</TableCell>
                                <TableCell style={{ width: 70 }}>
                                  <Chip size="small" className={classes.chipChain} label={chain || '—'} />
                                </TableCell>
                                <TableCell style={{ width: 130 }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                    <span style={{ fontSize: 12, color: '#cfd3ff', fontFamily: 'monospace' }}>{shortenAddress(primary)}</span>
                                    {!!primary && (
                                      <IconButton
                                        size="small"
                                        style={{ padding: 2, color: '#9ea4d8' }}
                                        title={`Copy address: ${primary}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          try { navigator.clipboard && navigator.clipboard.writeText(String(primary)).catch(() => {}); } catch (_) {}
                                        }}
                                      >
                                        <FileCopyIcon style={{ fontSize: 12 }} />
                                      </IconButton>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: '#e5e7ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {hook || story.title || ''}
                                  </div>
                                </TableCell>
                                <TableCell style={{ width: 50, textAlign: 'center' }}>
                                  {diveCount > 0 ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 6px', borderRadius: 10, background: 'rgba(77, 121, 255, 0.25)', border: '1px solid rgba(77, 121, 255, 0.45)', color: '#cfd3ff', fontSize: 11, fontWeight: 700 }}>
                                      {diveCount}
                                    </span>
                                  ) : ''}
                                </TableCell>
                                <TableCell style={{ width: 100 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <IconButton
                                      size="small"
                                      title="Open Links"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openLinksForStory(story);
                                      }}
                                      style={{ padding: 4, color: '#cfd3ff' }}
                                    >
                                      <OpenInNewIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      title="Find related stories"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        findRelatedForStory(story);
                                      }}
                                      style={{ padding: 4, color: '#cfd3ff' }}
                                    >
                                      <SearchIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      title="Export Brief"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        exportBriefForStory(story);
                                      }}
                                      style={{ padding: 4, color: '#cfd3ff' }}
                                    >
                                      <GetAppIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                  </div>
                                </TableCell>
                              </TableRow>

                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={8} style={{ padding: 12, background: 'rgba(20, 26, 55, 0.55)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                                      <Chip size="small" className={classes.chipType} label={story.story_type || 'STORY'} />
                                      <div style={{ fontSize: 14, color: '#e5e7ff', fontWeight: 800 }}>{story.title || 'Untitled story'}</div>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>{typeof score === 'number' ? `Score: ${score}` : (score !== '' ? `Score: ${score}` : '')}</div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 12, marginBottom: 10 }}>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>Hook</div>
                                      <div style={{ fontSize: 12, color: '#cfd3ff', whiteSpace: 'pre-wrap' }}>{hookFull || ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>What happened</div>
                                      <div style={{ fontSize: 12, color: '#cfd3ff', whiteSpace: 'pre-wrap' }}>{happened || ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>Why it matters</div>
                                      <div style={{ fontSize: 12, color: '#cfd3ff', whiteSpace: 'pre-wrap' }}>{matters || ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>Receipts</div>
                                      <div style={{ fontSize: 12, color: '#cfd3ff', whiteSpace: 'pre-wrap' }}>{receipts || ''}</div>
                                    </div>

                                    <StoryIntegrityBreakdown story={story} />

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                      <div style={{ fontSize: 12, color: '#9ea4d8' }}>Addresses</div>
                                    </div>
                                    <div style={{ maxHeight: 120, overflow: 'auto', border: '1px solid rgba(77, 121, 255, 0.18)', borderRadius: 10, padding: 8, background: 'rgba(10, 14, 30, 0.35)' }}>
                                      {addressList && addressList.length ? (
                                        addressList.slice(0, 60).map((addr, idx) => {
                                          const a = String(addr || '').trim();
                                          if (!a) return null;
                                          const role = idx === 0 ? 'Primary' : 'Related';
                                          return (
                                            <div key={`${sid}:${a}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                                              <div style={{ width: 70, fontSize: 11, color: '#9ea4d8' }}>{role}</div>
                                              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#cfd3ff' }}>{shortenAddress(a)}</div>
                                              <IconButton
                                                size="small"
                                                style={{ padding: 2, color: '#9ea4d8' }}
                                                title={`Copy address: ${a}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  try { navigator.clipboard && navigator.clipboard.writeText(String(a)).catch(() => {}); } catch (_) {}
                                                }}
                                              >
                                                <FileCopyIcon style={{ fontSize: 12 }} />
                                              </IconButton>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div style={{ fontSize: 12, color: '#9ea4d8' }}>No addresses found.</div>
                                      )}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.primaryButton}
                                        style={{ textTransform: 'none' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedId(story.id);
                                          setStoriesViewMode('cards');
                                        }}
                                      >
                                        View Evidence Graph →
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.primaryButton}
                                        style={{ textTransform: 'none' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          findRelatedForStory(story);
                                        }}
                                      >
                                        🔍 Find Related Stories
                                      </Button>
                                      <Button
                                        variant="contained"
                                        className={classes.primaryButton}
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          exportBriefForStory(story);
                                        }}
                                        style={{ minWidth: 0, padding: '4px 10px' }}
                                        title="Export Brief"
                                      >
                                        <GetAppIcon style={{ fontSize: 16 }} />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </div>
          </div>
        ) : forensicsFullScreen ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              height: '100vh',
              maxHeight: '100vh',
              overflow: 'hidden',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingTop: 'env(safe-area-inset-top)',
            }}
          >
            {renderForensicsPanel({ fullHeight: true })}
          </div>
        ) : (
          <div
            className={classes.mainGrid}
            style={isMobileLandscape ? { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' } : undefined}
          >
            {/* LEFT column: unified stories list */}
            <div className={classes.briefPanel}>
              <div className={classes.sectionTitleRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className={classes.sectionTitle}>Stories</div>
                  <div className={classes.sectionHint}>{updatedLabel}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flex: 1 }}>
                  <IconButton
                    size="small"
                    title="Move selected story up"
                    disabled={!selectedId}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      moveSelectedStory(-1);
                    }}
                    style={{ padding: 4, color: '#cfd3ff', opacity: selectedId ? 1 : 0.35 }}
                  >
                    <ChevronLeftIcon style={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Move selected story down"
                    disabled={!selectedId}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      moveSelectedStory(1);
                    }}
                    style={{ padding: 4, color: '#cfd3ff', opacity: selectedId ? 1 : 0.35 }}
                  >
                    <ChevronRightIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <IconButton
                    size="small"
                    title="Card view"
                    onClick={() => setStoriesViewMode('cards')}
                    style={{
                      padding: 4,
                      color: storiesViewMode === 'cards' ? '#ffffff' : '#9ea4d8',
                      border: storiesViewMode === 'cards'
                        ? '1px solid rgba(158, 196, 255, 0.9)'
                        : '1px solid transparent',
                      borderRadius: 8,
                    }}
                  >
                    <ViewAgendaIcon style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="List view"
                    onClick={() => setStoriesViewMode('list')}
                    style={{
                      padding: 4,
                      color: storiesViewMode === 'list' ? '#ffffff' : '#9ea4d8',
                      border: storiesViewMode === 'list'
                        ? '1px solid rgba(158, 196, 255, 0.9)'
                        : '1px solid transparent',
                      borderRadius: 8,
                    }}
                  >
                    <ViewListIcon style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Table view"
                    onClick={() => setStoriesViewMode('table')}
                    style={{
                      padding: 4,
                      color: storiesViewMode === 'table' ? '#ffffff' : '#9ea4d8',
                      border: storiesViewMode === 'table'
                        ? '1px solid rgba(158, 196, 255, 0.9)'
                        : '1px solid transparent',
                      borderRadius: 8,
                    }}
                  >
                    <TableChartIcon style={{ fontSize: 16 }} />
                  </IconButton>

                  <IconButton
                    onClick={() => setGroupModeEnabled((v) => !v)}
                    title={groupModeEnabled ? 'Group mode: ON' : 'Group mode: OFF'}
                    style={{
                      padding: 6,
                      color: groupModeEnabled ? '#9ef7c5' : '#cfd3ff',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 50,
                    }}
                  >
                    <PeopleIcon style={{ fontSize: 20 }} />
                    {groupedStoryIds && groupedStoryIds.size > 0 && (
                      <span style={{ marginLeft: 6, fontSize: 12, color: '#fff' }}>
                        {groupedStoryIds.size}
                      </span>
                    )}
                  </IconButton>

                  <IconButton
                    onClick={handleSelectAllGroupedStories}
                    disabled={[...briefStories, ...moreStories].length === 0}
                    title="Select all filtered stories"
                    style={{
                      padding: 6,
                      color: '#cfd3ff',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 50,
                      opacity: ([...briefStories, ...moreStories].length === 0) ? 0.35 : 1,
                    }}
                  >
                    <SelectAllIcon style={{ fontSize: 18 }} />
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      setGroupedStoryIds(new Set());
                      setGroupModeEnabled(false);
                    }}
                    disabled={(!groupedStoryIds || groupedStoryIds.size === 0) && !groupModeEnabled}
                    title="Reset group selection"
                    style={{
                      padding: 6,
                      color: '#cfd3ff',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 50,
                      opacity: ((!groupedStoryIds || groupedStoryIds.size === 0) && !groupModeEnabled) ? 0.35 : 1,
                    }}
                  >
                    <ClearIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </div>
              </div>
              <div id="moreStoriesPanel" className={classes.feedPanel}>
                <div
                  className={
                    storiesViewMode === 'list'
                      ? `${classes.feedBody} ${classes.feedBodySummary}`
                      : classes.feedBody
                  }
                >
                  {[...briefStories, ...moreStories].map((story) => {
                    const isSelected = story.id === selectedId;
                    const isGrouped = groupedStoryIds && groupedStoryIds.has(story.id);
                    const bodyText = getFullNarrative(story) || summarizeStory(story) || '';
                    const meta = story.meta || {};
                    const primaryIcon = meta.icon || meta.token_icon || null;

                    if (storiesViewMode === 'list') {
                      // In summary mode, render the selected story as the full
                      // expanded card and all others as compact one-line rows.
                      if (isSelected) {
                        const airdropMetrics = getAirdropMetricsLine(story);
                        const sandwichMetrics = getSandwichMetricsLine(story);
                        const primaryAddress = String(getLinksTarget(story).address || '').trim();
                        const primaryAddressDisplay = primaryAddress ? shortenAddress(primaryAddress) : '';
                        const addressList = storyAddresses && storyAddresses.get(story.id) ? (storyAddresses.get(story.id) || []) : [];
                        const otherAddresses = primaryAddress
                          ? addressList.filter((a) => String(a || '').toLowerCase() !== primaryAddress.toLowerCase()).slice(0, 30)
                          : [];
                        const relatedExpanded = !!(relatedAddressesExpandedById && relatedAddressesExpandedById[String(story.id)]);
                        return (
                          <div
                            key={story.id}
                            id={`story-item-${story.id}`}
                            data-story-id={story.id}
                            className={
                              storiesViewMode === 'list'
                                ? `${classes.feedRow} ${classes.feedRowSummary} ${classes.feedRowSelected}`
                                : `${classes.feedRow} ${classes.feedRowSelected}`
                            }
                            onClick={() => handleSelectStory(story)}
                          >
                            <div className={classes.feedRowTitleRow}>
                              {primaryIcon && (
                                <img
                                  src={primaryIcon}
                                  alt=""
                                  style={{ width: 16, height: 16, borderRadius: 5 }}
                                />
                              )}
                              <div className={classes.feedRowTitle}>{renderHighlightedText(story.title || 'Untitled story', search)}</div>
                              <IconButton
                                size="small"
                                title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}
                                onClick={(e) => toggleGroupStory(e, story)}
                                style={{ marginLeft: 'auto', padding: 4, color: isGrouped ? '#9ef7c5' : '#9ea4d8' }}
                              >
                                <PeopleIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </div>
                            <div className={classes.feedRowMeta}>
                              <Chip size="small" className={classes.chipType} label={story.story_type || 'STORY'} />
                              <span>{formatRelativeTime(story.ts_created)}</span>
                              {typeof story.normalizedScore === 'number' && (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <span>Score {Math.round(story.normalizedScore)}</span>
                                  <ScoreMethodologyTooltip
                                    glyph="sqrt"
                                    ariaLabel="How this story was measured"
                                    onActivate={(e) => {
                                      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                      handleSelectStory(story);
                                    }}
                                  />
                                </span>
                              )}
                              {story.id && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                  <span style={{ fontSize: 10, color: '#9ea4d8', fontFamily: 'monospace' }}>
                                    {String(story.id).length > 8 ? `${String(story.id).slice(0, 4)}…${String(story.id).slice(-4)}` : String(story.id)}
                                  </span>
                                  <IconButton
                                    size="small"
                                    style={{ padding: 2, color: '#9ea4d8' }}
                                    title={`Copy story ID: ${story.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      try { navigator.clipboard && navigator.clipboard.writeText(String(story.id)).catch(() => {}); } catch (_) {}
                                    }}
                                  >
                                    <FileCopyIcon style={{ fontSize: 10 }} />
                                  </IconButton>
                                </span>
                              )}
                            </div>
                            <div className={classes.teaser}>
                              <div>
                                {getKeyLinesForSummary(bodyText).length
                                  ? renderLabeledTeaser(getKeyLinesForSummary(bodyText).join(' '), search)
                                  : renderLabeledTeaser('Open to view the narrative and evidence.', search)}
                                {airdropMetrics && (
                                  <div className={classes.secondaryText} style={{ marginTop: 6 }}>
                                    {airdropMetrics}
                                  </div>
                                )}
                                {sandwichMetrics && (
                                  <div className={classes.secondaryText} style={{ marginTop: 6 }}>
                                    {sandwichMetrics}
                                  </div>
                                )}

                                {!!primaryAddress && (
                                  <div style={{ marginTop: 10 }}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      className={classes.primaryButton}
                                      style={{ textTransform: 'none' }}
                                      onClick={(e) => {
                                        if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                        setSearch(primaryAddress);

                                        try {
                                          const el = document.getElementById('storiesSearchField');
                                          if (el && typeof el.scrollIntoView === 'function') {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                          const input = el ? el.querySelector('input') : null;
                                          if (input && typeof input.focus === 'function') input.focus();
                                        } catch (err) {
                                        }
                                      }}
                                    >
                                      {`🔍 Find related stories for ${primaryAddressDisplay}`}
                                    </Button>

                                    {!!otherAddresses.length && (
                                      <div style={{ marginTop: 8 }}>
                                        <Button
                                          size="small"
                                          style={{ textTransform: 'none', color: '#9ea4d8', padding: 0, minWidth: 0 }}
                                          onClick={(e) => {
                                            if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                            if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                            const sid = story && story.id != null ? String(story.id) : '';
                                            if (!sid) return;
                                            setRelatedAddressesExpandedById((prev) => ({ ...(prev || {}), [sid]: !(prev && prev[sid]) }));
                                          }}
                                        >
                                          {relatedExpanded ? '− search by other addresses ▴' : '+ search by other addresses ▾'}
                                        </Button>

                                        {relatedExpanded && (
                                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {otherAddresses.map((addr) => {
                                              const a = String(addr || '').trim();
                                              if (!a) return null;
                                              return (
                                                <Button
                                                  key={a}
                                                  size="small"
                                                  variant="outlined"
                                                  style={{ textTransform: 'none', padding: '2px 8px', borderColor: 'rgba(158, 164, 216, 0.35)', color: '#cfd3ff' }}
                                                  onClick={(e) => {
                                                    if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                                    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                                    setSearch(a);
                                                    try {
                                                      const el = document.getElementById('storiesSearchField');
                                                      if (el && typeof el.scrollIntoView === 'function') {
                                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                      }
                                                      const input = el ? el.querySelector('input') : null;
                                                      if (input && typeof input.focus === 'function') input.focus();
                                                    } catch (err) {
                                                    }
                                                  }}
                                                >
                                                  {shortenAddress(a)}
                                                </Button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              {renderMatchedAddresses(story)}
                            </div>
                          </div>
                        );
                      }

                      // Compact summary row for non-selected stories.
                      return (
                        <div
                          key={story.id}
                          id={`story-item-${story.id}`}
                          data-story-id={story.id}
                          className={
                            storiesViewMode === 'list'
                              ? `${classes.feedRow} ${classes.feedRowSummary}`
                              : classes.feedRow
                          }
                          onClick={() => handleSelectStory(story)}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: '#4d79ff',
                                flexShrink: 0,
                              }}
                            />
                            <Chip size="small" className={classes.chipType} label={story.story_type || 'STORY'} />
                            <div
                              style={{
                                minWidth: 0,
                                flex: 1,
                                fontSize: 12,
                                color: '#e5e7ff',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {renderHighlightedText(story.title || 'Untitled story', search)}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexShrink: 0,
                                fontSize: 11,
                                color: '#9ea4d8',
                              }}
                            >
                              <IconButton
                                size="small"
                                title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}
                                onClick={(e) => toggleGroupStory(e, story)}
                                style={{ padding: 4, color: isGrouped ? '#9ef7c5' : '#9ea4d8' }}
                              >
                                <PeopleIcon style={{ fontSize: 16 }} />
                              </IconButton>
                              <span>{formatRelativeTime(story.ts_created)}</span>
                              {typeof story.normalizedScore === 'number' && (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <span>Score {Math.round(story.normalizedScore)}</span>
                                  <ScoreMethodologyTooltip
                                    glyph="sqrt"
                                    ariaLabel="How this story was measured"
                                    onActivate={(e) => {
                                      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                      handleSelectStory(story);
                                    }}
                                  />
                                </span>
                              )}
                              {story.id && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                  <span style={{ fontSize: 10, color: '#9ea4d8', fontFamily: 'monospace' }}>
                                    {String(story.id).length > 8 ? `${String(story.id).slice(0, 4)}…${String(story.id).slice(-4)}` : String(story.id)}
                                  </span>
                                  <IconButton
                                    size="small"
                                    style={{ padding: 2, color: '#9ea4d8' }}
                                    title={`Copy story ID: ${story.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      try { navigator.clipboard && navigator.clipboard.writeText(String(story.id)).catch(() => {}); } catch (_) {}
                                    }}
                                  >
                                    <FileCopyIcon style={{ fontSize: 10 }} />
                                  </IconButton>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                  // Default expanded card view (existing layout for cards mode).
                  const airdropMetrics = getAirdropMetricsLine(story);
                  const sandwichMetrics = getSandwichMetricsLine(story);
                  const primaryAddress = isSelected ? String(getLinksTarget(story).address || '').trim() : '';
                  const primaryAddressDisplay = primaryAddress ? shortenAddress(primaryAddress) : '';
                  const addressList = (isSelected && storyAddresses && storyAddresses.get(story.id)) ? (storyAddresses.get(story.id) || []) : [];
                  const otherAddresses = (isSelected && primaryAddress)
                    ? addressList.filter((a) => String(a || '').toLowerCase() !== primaryAddress.toLowerCase()).slice(0, 30)
                    : [];
                  const relatedExpanded = !!(isSelected && relatedAddressesExpandedById && relatedAddressesExpandedById[String(story.id)]);
                  return (
                    <div
                      key={story.id}
                      id={`story-item-${story.id}`}
                      data-story-id={story.id}
                      className={
                        isSelected
                          ? `${classes.feedRow} ${classes.feedRowSelected}`
                          : classes.feedRow
                      }
                      onClick={() => handleSelectStory(story)}
                    >
                      <div className={classes.feedRowTitleRow}>
                        {primaryIcon && (
                          <img
                            src={primaryIcon}
                            alt=""
                            style={{ width: 16, height: 16, borderRadius: 5 }}
                          />
                        )}
                        <div className={classes.feedRowTitle}>{renderHighlightedText(story.title || 'Untitled story', search)}</div>
                        {(Number(story && story.derived_count) > 0 || (derivedBySourceId && Array.isArray(derivedBySourceId[story.id]) && derivedBySourceId[story.id].length > 0)) && (
                          <Button
                            size="small"
                            style={{ marginLeft: 10, textTransform: 'none', color: '#9ea4d8' }}
                            onClick={(e) => {
                              if (e && typeof e.preventDefault === 'function') e.preventDefault();
                              if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                              const sid = story && story.id ? String(story.id) : '';
                              if (!sid) return;
                              const nextOpen = !(derivedOpenById && derivedOpenById[sid]);
                              setDerivedOpenById((prev) => ({ ...(prev || {}), [sid]: nextOpen }));
                              if (nextOpen) {
                                fetchDerivedStoriesForRow(sid);
                              }
                            }}
                          >
                            {(() => {
                              const sid = story && story.id ? String(story.id) : '';
                              const cachedLen = (sid && derivedBySourceId && Array.isArray(derivedBySourceId[sid])) ? derivedBySourceId[sid].length : 0;
                              const count = cachedLen || (Number(story && story.derived_count) || 0);
                              return (derivedOpenById && derivedOpenById[sid])
                                ? `Hide deep dives (${count})`
                                : `Deep dives (${count})`;
                            })()}
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}
                          onClick={(e) => toggleGroupStory(e, story)}
                          style={{ marginLeft: 'auto', padding: 4, color: isGrouped ? '#9ef7c5' : '#9ea4d8' }}
                        >
                          <PeopleIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </div>
                      <div className={classes.feedRowMeta}>
                        <Chip size="small" className={classes.chipType} label={story.story_type || 'STORY'} />
                        <span>{formatRelativeTime(story.ts_created)}</span>
                        {typeof story.normalizedScore === 'number' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <span>Score {Math.round(story.normalizedScore)}</span>
                            <ScoreMethodologyTooltip
                              glyph="sqrt"
                              ariaLabel="How this story was measured"
                              onActivate={(e) => {
                                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                handleSelectStory(story);
                              }}
                            />
                          </span>
                        )}
                        {story.id && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            <span style={{ fontSize: 10, color: '#9ea4d8', fontFamily: 'monospace' }}>
                              {String(story.id).length > 8 ? `${String(story.id).slice(0, 4)}…${String(story.id).slice(-4)}` : String(story.id)}
                            </span>
                            <IconButton
                              size="small"
                              style={{ padding: 2, color: '#9ea4d8' }}
                              title={`Copy story ID: ${story.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                try { navigator.clipboard && navigator.clipboard.writeText(String(story.id)).catch(() => {}); } catch (_) {}
                              }}
                            >
                              <FileCopyIcon style={{ fontSize: 10 }} />
                            </IconButton>
                          </span>
                        )}
                      </div>
                      <div className={classes.teaser}>
                        <div>
                          {renderLabeledTeaser(bodyText || 'Open to view the narrative and evidence.', search)}
                          {airdropMetrics && (
                            <div className={classes.secondaryText} style={{ marginTop: 6 }}>
                              {airdropMetrics}
                            </div>
                          )}
                          {sandwichMetrics && (
                            <div className={classes.secondaryText} style={{ marginTop: 6 }}>
                              {sandwichMetrics}
                            </div>
                          )}

                          {isSelected && !!primaryAddress && (
                            <div style={{ marginTop: 10 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                className={classes.primaryButton}
                                style={{ textTransform: 'none' }}
                                onClick={(e) => {
                                  if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                  setSearch(primaryAddress);

                                  try {
                                    const el = document.getElementById('storiesSearchField');
                                    if (el && typeof el.scrollIntoView === 'function') {
                                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                    const input = el ? el.querySelector('input') : null;
                                    if (input && typeof input.focus === 'function') input.focus();
                                  } catch (err) {
                                  }
                                }}
                              >
                                {`🔍 Find related stories for ${primaryAddressDisplay}`}
                              </Button>

                              {!!otherAddresses.length && (
                                <div style={{ marginTop: 8 }}>
                                  <Button
                                    size="small"
                                    style={{ textTransform: 'none', color: '#9ea4d8', padding: 0, minWidth: 0 }}
                                    onClick={(e) => {
                                      if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                      const sid = story && story.id != null ? String(story.id) : '';
                                      if (!sid) return;
                                      setRelatedAddressesExpandedById((prev) => ({ ...(prev || {}), [sid]: !(prev && prev[sid]) }));
                                    }}
                                  >
                                    {relatedExpanded ? '− search by other addresses ▴' : '+ search by other addresses ▾'}
                                  </Button>

                                  {relatedExpanded && (
                                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                      {otherAddresses.map((addr) => {
                                        const a = String(addr || '').trim();
                                        if (!a) return null;
                                        return (
                                          <Button
                                            key={a}
                                            size="small"
                                            variant="outlined"
                                            style={{ textTransform: 'none', padding: '2px 8px', borderColor: 'rgba(158, 164, 216, 0.35)', color: '#cfd3ff' }}
                                            onClick={(e) => {
                                              if (e && typeof e.preventDefault === 'function') e.preventDefault();
                                              if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                                              setSearch(a);
                                              try {
                                                const el = document.getElementById('storiesSearchField');
                                                if (el && typeof el.scrollIntoView === 'function') {
                                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }
                                                const input = el ? el.querySelector('input') : null;
                                                if (input && typeof input.focus === 'function') input.focus();
                                              } catch (err) {
                                              }
                                            }}
                                          >
                                            {shortenAddress(a)}
                                          </Button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {renderMatchedAddresses(story)}
                      </div>

                      {!!(derivedOpenById && derivedOpenById[String(story.id)]) && (
                        <div style={{ marginTop: 10, paddingLeft: 8, borderLeft: '2px solid rgba(77,121,255,0.35)' }}>
                          {derivedLoadingById && derivedLoadingById[String(story.id)] ? (
                            <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                              Loading deep dives…
                            </Typography>
                          ) : (derivedErrorById && derivedErrorById[String(story.id)]) ? (
                            <Typography variant="caption" style={{ color: '#ffb4b4', fontSize: 11 }}>
                              {derivedErrorById[String(story.id)]}
                            </Typography>
                          ) : (
                            <>
                              {Array.isArray(derivedBySourceId && derivedBySourceId[String(story.id)]) && derivedBySourceId[String(story.id)].length ? (
                                (derivedBySourceId[String(story.id)] || []).map((d, idx) => (
                                  <div key={d && d.id ? d.id : `derived_${story.id}_${idx}`} style={{ marginTop: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                      <Chip
                                        size="small"
                                        className={classes.chipType}
                                        label={(d && (d.question_type || d.story_type)) ? String(d.question_type || d.story_type) : 'DEEP_DIVE'}
                                        style={(() => {
                                          const t = (d && (d.question_type || d.story_type)) ? String(d.question_type || d.story_type) : '';
                                          const c = deepDiveColorFor(t);
                                          return { color: c, borderColor: c };
                                        })()}
                                      />
                                      <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                                        {d && d.ts_created ? formatRelativeTime(d.ts_created) : ''}
                                      </Typography>
                                    </div>
                                    <Typography variant="body2" style={{ color: '#e5e7ff', fontSize: 12, lineHeight: 1.5 }}>
                                      {d && d.title ? d.title : 'Deep dive'}
                                    </Typography>
                                    {!!(d && (getFullNarrative(d) || d.subtitle)) && (
                                      <Typography variant="caption" style={{ color: '#cfd3ff', fontSize: 11, display: 'block', marginTop: 3 }}>
                                        {renderHighlightedText(getFullNarrative(d) || d.subtitle || '', search)}
                                      </Typography>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <Typography variant="caption" style={{ color: '#9ea4d8', fontSize: 11 }}>
                                  No deep dives yet.
                                </Typography>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            </div>

            {/* RIGHT column: inline forensics panel (desktop + mobile landscape) */}
            {storiesViewMode !== 'table' && (!isMobile || !isPortrait) && renderForensicsPanel()}
          </div>
        )
      )}
      {/* In mobile portrait, also show the forensics panel stacked below the stories list,
          constrained to ~1/3 of the viewport height so it does not dominate the screen. */}
      {storiesViewMode !== 'table' && isMobile && isPortrait && !forensicsFullScreen && (
        <div className={classes.mobileForensicsWrapper}>
          {renderForensicsPanel({ fullHeight: true })}
        </div>
      )}
    </div>
  );
};
